import type { DataProvider, RaRecord } from 'react-admin';
import type { Block } from '../pageSchema/blocks';

/**
 * Consolidated TARGET schema for the admin data layer (step 0.5 — the model
 * consolidation, see DECISIONS.md D-schema-target and docs/schema-reconciliation.md).
 *
 * Under Path 1 (mock-first, D1) these types ARE the draft of the real shared
 * schema. They mirror the web app's drizzle backbone — names, enums and casing —
 * wherever the two overlap, and re-incorporate the old-platform concepts the
 * backbone lacks as properly-modelled first-class entities. Every such addition
 * is flagged `[EXTENSION]`: the real shared schema must grow into it before the
 * mock -> real swap (one-file change, see ./index.ts) stays a clean mapping.
 *
 * Casing rule (D-region-mapping): the admin ACCESS scope is uppercase
 * `UK | US | GLOBAL` (a Cognito attribute, see ../auth/permissions). The data
 * MARKET is lowercase `uk | us | ie` (a row attribute; no GLOBAL — "global" is
 * just the two/three market rows together). The data provider maps one to the
 * other in exactly one place.
 *
 * Surfaces import these types for typing only; they never import the seed. All
 * data access goes through the DataProvider.
 */

// --- shared vocabulary ------------------------------------------------------

/**
 * Concrete data markets a row can belong to. The seed is uk/us today, but the
 * type must not preclude `ie` (Ireland is on the roadmap) — the backbone's
 * rate_rules market enum is uk/us/ie/all.
 */
export type Market = 'uk' | 'us' | 'ie';

/**
 * The market vocabulary used by SCOPED config (the backbone's rate_rules.market):
 * a concrete market or `all`. Kept here so rate_rules (a Phase-3 surface, not
 * modelled yet) inherits the right vocabulary when it lands.
 */
export type RuleMarket = Market | 'all';

/** Settlement currency. Derived from market (D2): uk->gbp, us->usd, ie->eur. */
export type Currency = 'gbp' | 'usd' | 'eur';

/** The single market -> currency mapping (D2). */
export const CURRENCY_BY_MARKET: Record<Market, Currency> = {
  uk: 'gbp',
  us: 'usd',
  ie: 'eur',
};

/** Markets the seed actually populates today (uk/us); `ie` is type-reserved. */
export const SEED_MARKETS: readonly Market[] = ['uk', 'us'];

/** Every concrete market, in display order (uk, us, then the reserved ie). */
export const ALL_MARKETS: readonly Market[] = ['uk', 'us', 'ie'];

/**
 * THE one region -> markets mapping (D-region-mapping). The admin speaks the
 * uppercase ACCESS scope (`UK | US | GLOBAL`, a Cognito attribute); rows speak
 * the lowercase data `market`. This single function maps one to the other and is
 * the only place the mapping lives: the data provider applies it to the `region`
 * filter and to its aggregate seam, and the Earnings surface uses it to decide
 * which market bands a scope renders. An unknown scope grants nothing.
 */
export const marketsForScope = (scope: string): readonly Market[] => {
  switch (scope.toUpperCase()) {
    case 'UK':
      return ['uk'];
    case 'US':
      return ['us'];
    case 'GLOBAL':
      return ALL_MARKETS;
    default:
      return [];
  }
};

// --- merchants cluster ------------------------------------------------------

/** Coarse grouping — the backbone's flat merchant_category enum (7 values). */
export type MerchantCategory =
  | 'hair_and_beauty'
  | 'food'
  | 'travel'
  | 'fashion'
  | 'finance'
  | 'remittance'
  | 'other';

export type MerchantStatus = 'active' | 'paused' | 'archived';

/**
 * Verification state of a brand's Black-ownership evidence — the output of the
 * brand-research / vetting process. The backbone does not model ownership yet.
 * [EXTENSION — D-ownership]
 */
export type OwnershipVerificationStatus = 'unverified' | 'pending' | 'verified';

/** Brand-level, market-agnostic. The old flat `store` splits brand/market/channel. */
export interface Merchant extends RaRecord<number> {
  name: string;
  slug: string;
  /** Longform brand description (old store description). */
  description: string;
  /** Brand-level terms & conditions. */
  terms: string;
  /** FK -> categories.id. Supersedes the flat enum (D3). */
  categoryId: number;
  /** Coarse grouping retained as migration source / rollup (D3). */
  coarseCategory: MerchantCategory;
  logoUrl: string;
  status: MerchantStatus;
  /**
   * How cashback is tracked for this brand (backbone merchants.tracking_method).
   * Regions/channels may carry an override. Lives on the merchant, not the channel.
   */
  trackingMethod: TrackingMethod;
  /** Who funds the cashback (backbone merchants.cashback_funding_source). */
  cashbackFundingSource: CashbackFundingSource;
  /**
   * How transactions are attributed for this brand (backbone
   * merchants.attribution_method). Regions/channels may carry an override; the
   * resolved value is snapshotted on each click, never on the transaction.
   */
  attributionMethod: AttributionMethod;
  /**
   * Name(s) of the brand's founder(s). Central to Cashblack's premise — a
   * platform for Black-owned brands — and captured by the brand-research
   * process. The backbone does not model ownership yet. [EXTENSION — D-ownership]
   */
  founderNames: string[];
  /** The founders' / brand's country of heritage. [EXTENSION — D-ownership] */
  countryOfHeritage: string;
  /**
   * Link to the evidence used to verify Black ownership (the brand-research
   * dossier / source). [EXTENSION — D-ownership]
   */
  blackOwnershipEvidenceUrl: string;
  /** State of the ownership-evidence verification. [EXTENSION — D-ownership] */
  ownershipVerificationStatus: OwnershipVerificationStatus;
  createdAt: string;
  /**
   * DERIVED — total clicks summed across this merchant's regions. Injected by
   * the provider from the clicks collection; not stored in the seed.
   */
  clicks?: number;
}

/**
 * A live rate boost on a merchant_region's resolved rate — the metadata the
 * Earnings surface reads to mark a boosted entity ("boosted, ends 2 Jan") and to
 * explain a thin retained margin as INTENTIONAL rather than a mispriced deal.
 *
 * A boost is conceptually a `rate_rules` row (an `effect_type` multiplier inside
 * a visibility window); `rate_rules` is a Phase-3 surface not modelled yet, so
 * for v1 the resolved boost is carried on the region it applies to. The fields
 * mirror the backbone's rate-rule display columns: `label` = `display_label`,
 * `reasonCode` = `display_reason_code`, `endsAt` = `visible_until`.
 * [EXTENSION - D-earnings-boost]
 */
export interface RateBoost {
  label: string;
  reasonCode: string;
  /** When the boost ends (ISO). A boost is LIVE while `endsAt` is in the future. */
  endsAt: string;
}

/** One row per market a merchant operates in. Global = the rows together. */
export interface MerchantRegion extends RaRecord<number> {
  /** FK -> merchants.id */
  merchantId: number;
  market: Market;
  /**
   * % member cashback rate for this market (backbone merchant_regions.base_rate,
   * numeric(6,4) — percentage units to 4 dp, e.g. 5.0000 = 5%).
   */
  baseRate: number;
  /** True when baseRate was set manually rather than derived (old cashbackOverridden). */
  baseRateOverridden: boolean;
  /** FK -> affiliateNetworks.id, or null (marketplace-only / direct brand deal). [EXTENSION] */
  networkId: number | null;
  /**
   * % commission the network pays the platform for this market — the margin
   * source the old Earnings report needs, distinct from the member baseRate
   * (numeric(6,4), 4 dp). Null when there is no affiliate network.
   * [EXTENSION — D-commission]
   */
  networkCommissionRate: number | null;
  /** Market-specific terms (backbone merchant_regions.regionTerms). */
  regionTerms: string;
  /** Region-level enable flag (backbone merchant_regions.active — a boolean, not a status enum). */
  active: boolean;
  /** Override of the merchant's tracking method for this market, or null to inherit. */
  trackingMethodOverride: TrackingMethod | null;
  /** Override of the merchant's attribution method for this market, or null to inherit. */
  attributionMethodOverride: AttributionMethod | null;
  /**
   * Denormalised mirror of merchantRegionTags (the tag KEYS) for mock filter
   * convenience. The join table is the real-schema target. [EXTENSION — D-tags]
   */
  tagKeys: string[];
  /**
   * A live rate boost on this region's rate, or null when the rate is not
   * boosted. Read by the Earnings surface to mark boosted entities and to read
   * their thin margin as intentional. [EXTENSION - D-earnings-boost]
   */
  boost: RateBoost | null;
  createdAt: string;
  /** DERIVED — click count for this region. Provider-injected, not stored. */
  clicks?: number;
}

/**
 * How cashback is tracked for a channel — the backbone `tracking_method` enum
 * (verified against docs/backbone-schema.md). `clo` = card-linked offer,
 * `ob` = order-based, `multi_rail` = more than one method, `none` = untracked.
 */
export type TrackingMethod = 'affiliate' | 'clo' | 'ob' | 'multi_rail' | 'none';

/**
 * Who funds the cashback — the backbone `cashback_funding_source` enum. Lives on
 * the merchant.
 */
export type CashbackFundingSource =
  | 'affiliate_commission'
  | 'merchant_direct_clo'
  | 'merchant_direct_ob';

/**
 * The CHANNEL / marketplace a brand sells through in a market — the backbone
 * `store_channels.channel_type` enum (verified against docs/backbone-schema.md).
 * `direct` is the brand's own storefront and `brick_and_mortar` is in store;
 * the rest are MARKETPLACES the brand sells through. The marketplace identity is
 * this stable enum VALUE, not a separate marketplaces collection (the backbone
 * has none either, so a first-class collection would lead the backbone — it is
 * deferred; D-multi-channel-editor). Rate rules scope by this value, the same
 * stable-key pattern as tag-key scoping.
 *
 * The storefront marketplaces etsy / amazon / ebay / doordash extend the
 * backbone enum (the backbone must grow the same four values; see
 * docs/schema-reconciliation.md). Affiliate / content aggregators (Digidip,
 * Skimlinks, Sovrn) are deliberately NOT channels: they live on the separate
 * affiliateNetworks axis. [EXTENSION — D-multi-channel-editor]
 */
export type ChannelType =
  | 'direct'
  | 'deliveroo'
  | 'uber_eats'
  | 'just_eat'
  | 'brick_and_mortar'
  | 'etsy'
  | 'amazon'
  | 'ebay'
  | 'doordash';

/**
 * Every channel type in display order, the source for the "add a channel"
 * picker. The marketplace subset (everything but `direct`) is what a brand sells
 * THROUGH; `direct` is its own storefront.
 */
export const CHANNEL_TYPE_VALUES: readonly ChannelType[] = [
  'direct',
  'deliveroo',
  'uber_eats',
  'just_eat',
  'brick_and_mortar',
  'etsy',
  'amazon',
  'ebay',
  'doordash',
];

/**
 * Human display name per channel type. The enum VALUE stays the stable ref (the
 * thing rate rules scope by); the label is presentation only. The seed reads the
 * same map so seeded labels and the UI never drift.
 */
export const CHANNEL_TYPE_LABELS: Record<ChannelType, string> = {
  direct: 'Direct',
  deliveroo: 'Deliveroo',
  uber_eats: 'Uber Eats',
  just_eat: 'Just Eat',
  brick_and_mortar: 'In store',
  etsy: 'Etsy',
  amazon: 'Amazon',
  ebay: 'eBay',
  doordash: 'DoorDash',
};

/** Per-channel presence within a merchant_region (backbone store_channels). */
export interface StoreChannel extends RaRecord<number> {
  /** FK -> merchantRegions.id (confirmed: backbone storeChannels.merchantRegionId). */
  merchantRegionId: number;
  /** Denormalised market mirror for uniform region scoping (real schema joins). */
  market: Market;
  channelType: ChannelType;
  /** Human label (backbone store_channels.label). */
  label: string;
  /** % per-channel cashback rate (numeric(6,4), 4 dp; overrides the region baseRate). */
  baseRate: number;
  /**
   * The affiliate / tracking deep link (the old store storeUrl lands here).
   * Nullable: auto/manual channels mint the URL at click time (backbone
   * store_channels.tracking_url). There is NO tracking_method column here — that
   * lives on the merchant.
   */
  trackingUrl: string | null;
  /** Override of the merchant's attribution method for this channel, or null to inherit. */
  attributionMethodOverride: AttributionMethod | null;
  /** Channel enable flag (backbone store_channels.is_active — a boolean). */
  isActive: boolean;
  createdAt: string;
}

/** First-class tag vocabulary — feeds rate-rule scoping & placement. [EXTENSION — D-tags] */
export interface StoreTag extends RaRecord<number> {
  /** Stable key referenced by rate_rules.scopeRef, e.g. 'top_stores'. */
  key: string;
  label: string;
  description: string;
}

/** Tag assignment — per-market placement (join). [EXTENSION — D-tags] */
export interface MerchantRegionTag extends RaRecord<number> {
  /** FK -> merchantRegions.id */
  merchantRegionId: number;
  /** FK -> storeTags.id */
  tagId: number;
}

// --- rate rules -------------------------------------------------------------

/**
 * What a rate rule applies to (backbone `rule_scope_type`). The marketplace
 * scopes reference channelType ENUM VALUES (the stable ref established by the
 * multi-channel editor); the tag scopes reference a `storeTags.key`; category
 * references `merchants.categoryId` (a BRAND-level hop, since categoryId lives on
 * the merchant, not the region). [EXTENSION — D-rate-rules]
 */
export type RuleScopeType =
  | 'store'
  | 'marketplace'
  | 'category'
  | 'marketplace_category'
  | 'tag'
  | 'tag_marketplaces'
  | 'store_list'
  | 'market';

/**
 * How a rule transforms the base rate (backbone `rule_effect_type`).
 *  - multiplier: effectiveRate = baseRate * effectValue
 *  - absolute:   effectiveRate = effectValue (force a rate)
 *  - cap:        effectiveRate = min(baseRate, effectValue) (ceiling)
 *  - floor:      effectiveRate = max(baseRate, effectValue) (floor)
 * [EXTENSION — D-rate-rules]
 */
export type RuleEffectType = 'multiplier' | 'absolute' | 'cap' | 'floor';

/**
 * A rule's lifecycle status. A MOCK EXTENSION over the backbone, which has NO
 * active flag and governs liveness only by the visibility window. The `status`
 * governs whether a rule is live AT ALL, the window governs WHEN a live rule
 * applies. [EXTENSION — D-rate-rules]
 */
export type RuleStatus = 'draft' | 'active' | 'paused';

/**
 * The scope reference (backbone `scope_ref` jsonb). Its shape DEPENDS on
 * `scopeType`:
 *  - store: a merchantRegion id (number)
 *  - marketplace: a channelType value
 *  - category: a categoryId (number)
 *  - marketplace_category: { channelType, categoryId }
 *  - tag: a storeTags.key (string)
 *  - tag_marketplaces: { tag: key, marketplaces: channelType[] }
 *  - store_list: merchantRegion id[] (number[])
 *  - market: a market value (uk/us/ie)
 * [EXTENSION — D-rate-rules]
 */
export type RuleScopeRef =
  | number
  | string
  | number[]
  | { channelType: ChannelType; categoryId: number }
  | { tag: string; marketplaces: ChannelType[] };

/**
 * A rate rule (backbone `rate_rules`). The admin AUTHORS it (scope, effect, tier
 * eligibility, market, priority, window); the BACKEND EVALUATES it per click
 * (computes the effective rate, decides which rule wins). The admin surface
 * PREVIEWS the engine's output over today's catalog without BEING the engine.
 * [EXTENSION — D-rate-rules]
 */
export interface RateRule extends RaRecord<number> {
  scopeType: RuleScopeType;
  scopeRef: RuleScopeRef;
  effectType: RuleEffectType;
  /**
   * numeric(8,4): a multiplier factor (1.5 = +50%) for `multiplier`, or a rate in
   * percentage units for `absolute` / `cap` / `floor`.
   */
  effectValue: number;
  /** Tiers the rule applies to (backbone `tier_eligibility`). */
  tierEligibility: UserTier[];
  /** Market scope (backbone `rule_market`): a concrete market or `all`. */
  market: RuleMarket;
  /** Explicit precedence (backbone `priority`). Higher wins. Required, never a silent 0. */
  priority: number;
  /** Visibility window (backbone `visible_from` / `visible_until`). A null bound is open. */
  visibleFrom: string | null;
  visibleUntil: string | null;
  /** Member-facing display metadata (backbone `display_label` / `display_reason_code`). */
  displayLabel: string | null;
  displayReasonCode: string | null;
  /** Lifecycle (the mock extension over the window). */
  status: RuleStatus;
  /** Who authored the rule (backbone `created_by`). */
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// --- page schemas (schema-driven block editor) ------------------------------

/**
 * A page schema's lifecycle. `draft` is the only reachable state in sub-surface
 * 8a (authoring); `published` (with versioned history and rollback) is 8b. The
 * field is modelled now so the row shape is stable, but no publish path exists
 * yet. [EXTENSION — D-page-schema-editor-8a]
 */
export type PageSchemaStatus = 'draft' | 'published';

/**
 * A per-block visibility window (D-page-schema-editor-8d). Both bounds are nullable
 * for an open window (only-from, only-until, or neither). It lives ALONGSIDE the block
 * (a blockSchedules map keyed by block id), NOT inside the mirrored BlockSchema: the
 * window is admin / serving metadata, not a render-contract field (the real
 * @cashblack/contracts block carries no scheduling, and the apps render PRE-FILTERED
 * blocks). [EXTENSION - D-page-schema-editor-8d]
 */
export interface BlockWindow {
  visibleFrom: string | null;
  visibleUntil: string | null;
}

/**
 * Per-block schedules keyed by the stable block id. A block with NO entry (or both
 * bounds null) is ALWAYS visible. Keyed by id (not a parallel array) so reorder /
 * remove stay trivial; a removed block's entry is dropped. [EXTENSION - 8d]
 */
export type BlockSchedules = Record<string, BlockWindow>;

/**
 * One PUBLISHED version of a page (D-page-schema-editor-8b). The version history is
 * APPEND-ONLY and never mutated or reordered: each publish (and each rollback, which
 * is itself a publish of a prior snapshot) APPENDS one of these. The `blocks` is a
 * FULL snapshot of what went live, so the forward "what was live when" trail is
 * complete. The CURRENT PUBLISHED is DERIVED as the last entry (the ledger-current
 * discipline, deriveBalanceSigned), never a stored pointer. [EXTENSION - D-page-
 * schema-editor-8b]
 */
export interface PublishedVersion {
  /** Monotonic publish counter (increments on EVERY publish, including a rollback). */
  version: number;
  /** A full snapshot of the blocks that went live at this version. */
  blocks: Block[];
  /**
   * A snapshot of the per-block schedules that went live with this version (8d). A
   * published page is served TIME-FILTERED by these windows, so the snapshot must
   * travel with the blocks (else a rollback would restore blocks without their
   * windows). Optional: a version published before 8d, or with no windows, omits it.
   */
  blockSchedules?: BlockSchedules;
  publishedAt: string;
  publishedBy: string;
}

/**
 * A schema-driven page (backbone has NO page_schema table; this is mock-leads-
 * backbone, see docs/schema-reconciliation.md). The admin AUTHORS a draft of
 * `blocks` per (pageId, market); the consumer apps RENDER the published schema.
 * `blocks` is the LOCKED block contract mirrored in ../pageSchema/blocks.ts (the
 * cross-repo swap seam).
 *
 * VERSIONING (D-page-schema-editor-8b): the top-level `blocks` is the editable DRAFT
 * (the composer edits it). `versions` is the APPEND-ONLY published history; the
 * CURRENT PUBLISHED is DERIVED as the last entry (what the apps serve), never a
 * stored pointer. The top-level `status` / `version` / `publishedAt` reflect the
 * latest publish: `status` flips to 'published' once `versions` is non-empty,
 * `version` is the latest version int and `publishedAt` the latest publish time. An
 * unpublished draft carries status 'draft', version 0, publishedAt null and an empty
 * `versions`. [EXTENSION - D-page-schema-editor-8a / 8b]
 */
export interface PageSchema extends RaRecord<number> {
  /** The page id (vocabulary: home, browse-featured, category-{slug}, seasonal-{name}). */
  pageId: string;
  /** The market the schema is authored for (operational scoping). */
  market: Market;
  /** Lifecycle. 'draft' until the first publish, then 'published' (rollback keeps it). */
  status: PageSchemaStatus;
  /** The latest published version int. 0 for an unpublished draft; set at publish. */
  version: number;
  /** The latest publish time, or null for an unpublished draft. */
  publishedAt: string | null;
  /** The editable DRAFT blocks (the LOCKED contract; ../pageSchema/blocks.ts). */
  blocks: Block[];
  /**
   * The DRAFT per-block schedules (8d), keyed by block id, ALONGSIDE the blocks (NOT
   * inside the render-union block). Optional: a block with no entry is always visible.
   * Snapshotted into each PublishedVersion at publish.
   */
  blockSchedules?: BlockSchedules;
  /** The APPEND-ONLY published version history (8b). Empty until the first publish. */
  versions: PublishedVersion[];
  /** Who authored the draft. */
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// --- CMS pages (conventional rich-text CMS) ---------------------------------

/**
 * A TipTap (ProseMirror) rich-text document, the CANONICAL body shape for CMS content
 * (D-cms-pages-core). A recursive node tree: a `doc` node whose `content` is the block
 * nodes (paragraph, heading, bulletList, image, the custom cta node, ...), each node
 * carrying optional `attrs`, `content`, `marks` and leaf `text`. The mock LEADS this
 * shape (no /cms body contract exists in cashblack-web, grep-confirmed); the real
 * marketing-site contract reconciles against it at migration (the swap seam). HTML
 * emission is a render / migration concern, NOT stored here. [EXTENSION - D-cms-pages-core]
 */
export interface TipTapMark {
  type: string;
  attrs?: Record<string, unknown>;
}

export interface TipTapNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: TipTapNode[];
  marks?: TipTapMark[];
  text?: string;
}

export interface TipTapDoc {
  type: 'doc';
  content: TipTapNode[];
}

/**
 * A LIGHT grouping field, NOT a content-shape switch. The legacy System / General /
 * Special types are a legacy UI choice and are NOT carried as a data switch; this just
 * groups the editable marketing documents. [EXTENSION - D-cms-pages-core]
 */
export type CmsPageKind = 'legal' | 'marketing' | 'help';

/** A CMS page targets a single market OR both (the RuleMarket idiom). */
export type CmsPageMarket = Market | 'all';

/** Simple draft / published status (NOT the page-schema versioned lifecycle). */
export type CmsPageStatus = 'draft' | 'published';

/**
 * A conventional rich-text marketing page (D-cms-pages-core). SEPARATE from
 * `PageSchema`: that is the app's schema-driven screens (typed blocks); this is a
 * marketing DOCUMENT authored as rich text. Distinct collection, content model
 * (TipTap JSON, not blocks), renderer (marketing site, not the app block renderer) and
 * purpose. The lifecycle is a SIMPLE status (publish makes live, edit returns to draft,
 * NO versioned history or rollback; the versioned lifecycle is the recorded governance
 * upgrade path). MOCK-LEADS the backbone (no cms_pages table). [EXTENSION - D-cms-pages-core]
 */
export interface CmsPage extends RaRecord<number> {
  title: string;
  /** Unique per market. */
  slug: string;
  kind: CmsPageKind;
  /** uk / us / ie or 'all' (both markets); composes with marketsForScope by membership. */
  market: CmsPageMarket;
  status: CmsPageStatus;
  /** The canonical TipTap (ProseMirror) JSON body. */
  body: TipTapDoc;
  /** Set at publish, null for a draft. */
  publishedAt: string | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * A managed editorial-category vocabulary for the blog (D-cms-blog). Cloned EXACTLY from
 * the `StoreTag` vocabulary: a stable immutable `key` a post references, a display
 * `label` and a `description`. Create and edit only, NO delete (a category a post
 * references must not vanish; a retire flag is a future additive). This is editorial
 * grouping (news, guides, brand-spotlight), DISTINCT from the merchant product
 * `Category` tree (conflation rejected) and from a hardcoded enum (editorial categories
 * grow without a release). Platform-wide reference data, NOT market-scoped. MOCK-LEADS
 * the backbone (no blog_categories table). [EXTENSION - D-cms-blog]
 */
export interface BlogCategory extends RaRecord<number> {
  /** The stable key a post references, e.g. 'brand-spotlight'. Immutable after create. */
  key: string;
  label: string;
  description: string;
}

/**
 * A conventional rich-text blog post (D-cms-blog). REUSES the `CmsPage` spine (title,
 * slug, market, status, the SAME TipTap `body`, the SAME simple draft / published
 * lifecycle) in a SEPARATE collection that adds the blog-specific fields. It is NOT a
 * `kind` on `CmsPage`: a post carries a category, a featured image, a byline and an
 * excerpt a page does not, so folding them onto the page row would bloat it (the same
 * separation as cmsPages vs pageSchemas). MOCK-LEADS the backbone (no blog table).
 * [EXTENSION - D-cms-blog]
 */
export interface BlogPost extends RaRecord<number> {
  title: string;
  /** Unique per market. */
  slug: string;
  /** uk / us / ie or 'all' (both markets); the CmsPageMarket shape, composes by membership. */
  market: CmsPageMarket;
  status: CmsPageStatus;
  /** A single blogCategories key (single-category per post; multi is a later additive). */
  categoryKey: string;
  /** A URL string (consistent with the body image deferral; NOT an asset upload). */
  featuredImageUrl: string;
  /** The byline NAME (editorial; may credit a brand or guest), distinct from createdBy. */
  author: string;
  /** An optional summary for the post list and social / SEO previews. */
  excerpt: string;
  /** The canonical TipTap (ProseMirror) JSON body, authored by the SAME RichTextEditor. */
  body: TipTapDoc;
  /** Set at publish, null for a draft. ALSO the displayed post date (no separate date). */
  publishedAt: string | null;
  /** The acting admin (the audit field), distinct from the editorial `author` byline. */
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Simple on / off, the deactivate-not-delete toggle (the CharityStatus pattern). */
export type TestimonialStatus = 'active' | 'inactive';

/**
 * A marketing-site testimonial (D-cms-testimonials-seo, Phase 8, CMS sub-surface c1). A
 * FLAT structured record, NO rich text and NO publish lifecycle: a testimonial is on or
 * off (an `active` / `inactive` toggle, deactivate-not-delete, the charities pattern),
 * NOT an authored-then-published document. The `author` is a byline NAME string (the
 * editorial credit, like a blog byline), NOT a user FK. MOCK-LEADS the backbone (no
 * testimonials table). [EXTENSION - D-cms-testimonials-seo]
 */
export interface Testimonial extends RaRecord<number> {
  quote: string;
  /** The byline NAME (editorial credit), NOT a user FK. */
  author: string;
  /** A 1 to 5 star rating. */
  rating: number;
  /** uk / us / ie or 'all' (both markets); composes with marketsForScope by membership. */
  market: CmsPageMarket;
  /** The on / off toggle (deactivate-not-delete), routed through the chokepoint. */
  status: TestimonialStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * A per-page SEO meta rule (D-cms-testimonials-seo, Phase 8, CMS sub-surface c1). It
 * keys to a FREE `pageKey` / path string, NOT a foreign key into any one collection: the
 * admin has FOUR page-identifier shapes that share NO id space (cmsPages.slug,
 * blogPosts.slug, pageSchemas.pageId and system routes in no collection), so a FK into
 * one would structurally miss the others. Every page resolves to a URL, so a free path
 * (e.g. 'about', 'blog/cashblack-launches-uk', 'home', 'stores') covers cmsPages AND
 * blogPosts AND pageSchemas AND system routes uniformly. The key is NOT validated against
 * a collection (it may name a system route the CMS does not own). The "region variants"
 * are the `market` field: the SAME pageKey carries a uk row AND a us row (uniqueness is
 * per pageKey + market). A simple managed record, NO publish lifecycle (meta is set, not
 * authored-then-published; an absent rule falls back to default meta). MOCK-LEADS the
 * backbone (no seo_rules table). [EXTENSION - D-cms-testimonials-seo]
 */
export interface SeoRule extends RaRecord<number> {
  /** The free page path / key target (NOT a FK; covers all page-identifier types). */
  pageKey: string;
  metaTitle: string;
  metaDescription: string;
  /** A URL string (the image-deferral shape; NOT an asset upload). */
  ogImageUrl: string;
  /** uk / us / ie or 'all'; the region variant IS this field (same pageKey, per-market rows). */
  market: CmsPageMarket;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** A slider slide's call-to-action: a PLAIN label plus a plain URL link. */
export interface SliderCta {
  label: string;
  link: string;
}

/**
 * A marketing-site slider slide (D-cms-sliders, Phase 8, CMS sub-surface c2). A slide is
 * an authored image plus a CTA that lives in a marketing-site PLACEMENT (a free CMS-owned
 * key, e.g. 'homepage-hero'), with an `order` WITHIN that placement (per-placement, NOT
 * global). It is MARKETING-SITE CMS data, DISTINCT from the merchant `Placement` (a brand
 * booking on a backbone PlacementSlot with a weight and window, no authored content) AND
 * from the app's carousel / banner blocks (the locked block contract). NO publish: a
 * slider is active-and-ordered, the active toggle is the whole lifecycle (like
 * testimonials). The `cta` is a PLAIN { label, link } (a marketing slide links to a plain
 * URL), NOT the app blocks' { kind, ref } link. MOCK-LEADS the backbone (no slider table).
 * [EXTENSION - D-cms-sliders]
 */
export interface SliderSlide extends RaRecord<number> {
  /** The marketing-site placement, a FREE CMS-owned key (NOT the merchant PlacementSlot). */
  placement: string;
  /** A URL string (the image-deferral shape; NOT an asset upload). */
  imageUrl: string;
  /** A plain { label, link } CTA (a marketing slide links to a plain URL). */
  cta: SliderCta;
  /**
   * The slide's position WITHIN its placement (per-placement, NOT global). An 'all'-market
   * slide carries a SINGLE shared order integer, the same in every scope (the accepted mock
   * behaviour; an order-per-market map is the deferred refinement, see DEBT.md).
   */
  order: number;
  /** uk / us / ie or 'all'; composes with marketsForScope by membership. */
  market: CmsPageMarket;
  /** The on / off toggle (deactivate-not-delete), routed through the chokepoint. */
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** What a menu link points at. INTERNAL (page / category / store) vs EXTERNAL (a URL). */
export type MenuLinkKind = 'page' | 'category' | 'store' | 'external';

/**
 * A menu item's link target (D-cms-menu). The `{ kind, ref }` shape reused CONCEPTUALLY
 * from the page-schema LinkSchema, NOT imported from the locked blocks.ts (the menu is
 * mock-leads CMS data). `kind` distinguishes an INTERNAL target (page / category / store,
 * `ref` a FREE path / slug / key, the SEO posture, NOT validated against a collection) from
 * an EXTERNAL one (`kind: 'external'`, `ref` a URL). This internal-vs-external distinction
 * is what a menu needs and what the SEO free-path alone could not express.
 */
export interface MenuLink {
  kind: MenuLinkKind;
  /** A free path / slug / key for an internal kind, or a URL for 'external'. */
  ref: string;
}

/**
 * A navigation menu item (D-cms-menu, Phase 8, CMS sub-surface d, the LAST CMS piece). A
 * nested link TREE, an ADJACENCY LIST (parentId + order, the Category-tree shape) CAPPED at
 * 2 LEVELS (a top-level item plus one level of children; a child cannot itself be a
 * parent). Each item belongs to a `location` (a FREE CMS-owned key, e.g. 'header' /
 * 'footer'), has an `order` WITHIN its level, an optional `link` (absent for a non-linking
 * parent heading that only groups its children) and a per-item `market`. MARKETING-SITE CMS
 * data; NO publish (active-and-ordered, the active toggle is the whole lifecycle, like
 * sliders). MOCK-LEADS the backbone (no menu table). [EXTENSION - D-cms-menu]
 */
export interface MenuItem extends RaRecord<number> {
  /** The menu location, a FREE CMS-owned key (e.g. 'header' / 'footer'). */
  location: string;
  /** Parent item id, or null for a top-level item (the adjacency-list shape, 2-level cap). */
  parentId: number | null;
  /** The item's position WITHIN its level (top-level or its parent's children). */
  order: number;
  /** The displayed menu text. */
  label: string;
  /**
   * The link target, or absent for a non-linking parent heading. An 'all'-market item
   * sharing a level with single-market items carries a SINGLE shared order (the slider
   * 'all'-shared behaviour; the order-per-market map is the deferred refinement, DEBT.md).
   */
  link?: MenuLink;
  /** uk / us / ie or 'all'; per-item, composes with marketsForScope by membership. */
  market: CmsPageMarket;
  /** The on / off toggle (deactivate-not-delete), routed through the chokepoint. */
  active: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/** Simple active / inactive (deactivate-not-delete, the charities pattern). */
export type OfflineStoreStatus = 'active' | 'inactive';

/**
 * The CLO matching config for an offline venue (D-offline-stores). The merchant-matching
 * identifiers the card networks key on to attribute a member's enrolled-card transaction
 * at the venue, DISTINCT from the online affiliate `networkId` credentials. The matching
 * also uses the venue postcode. AUTHORING only: the admin records these parameters; the
 * actual CLO crediting (matching a real transaction) is the backend / PayoutEngine.
 */
export interface CardLinkConfig {
  /** The card-network merchant category code. */
  mcc: string;
  /** The optional acquirer / terminal merchant id. */
  mid?: string;
  /** Whether the venue is part of a chain (affects matching breadth). */
  isChain: boolean;
}

/**
 * A brick-and-mortar / card-linked-offer VENUE (D-offline-stores, Phase 8). ITS OWN
 * collection, ONE row per physical venue: the brand -> region -> channel merchant model
 * (Merchant / MerchantRegion / StoreChannel) holds ONE brick_and_mortar channel per region
 * and cannot carry a per-venue cardinality (a brand has many venues), and the offline-only
 * fields (a physical location, the CLO config, the IRL flag) apply only here, so this is a
 * SEPARATE collection, NOT a merchant extension. An OPTIONAL `merchantId` links to an online
 * brand (reuse its identity when the venue belongs to a catalog brand; standalone, its own
 * name, when offline-only). Tracking is the offline rail (`clo` / `ob`), REUSING the
 * existing TrackingMethod enum. AUTHORING only (the crediting is the backend). MOCK-LEADS
 * the backbone (no venue table). [EXTENSION - D-offline-stores]
 */
export interface OfflineStore extends RaRecord<number> {
  /** The venue name (the brand's name if linked, or its own if standalone). */
  name: string;
  /** Optional FK -> merchants.id, or null for an offline-only venue. */
  merchantId: number | null;
  /** The street address (flat, NOT a structured sub-object). */
  addressLine: string;
  /** The postcode / ZIP (the search key). */
  postcode: string;
  /** The VENUE's country (distinct from the brand's countryOfHeritage; do NOT conflate). */
  country: string;
  /** Single market by nature (a physical shop is in one country); NOT Market | 'all'. */
  market: Market;
  /** The offline tracking rail: 'clo' (card-linked offer) or 'ob' (Open Banking). */
  trackingMethod: TrackingMethod;
  /** The CLO matching config (authoring only); the affiliate model's networkId has no place here. */
  cardLinkConfig: CardLinkConfig;
  /** Marks a pop-up / event venue vs a permanent store (the time-bounding is deferred). */
  irlEvent: boolean;
  /** active / inactive (deactivate-not-delete); a closed venue deactivates, never hard-deletes. */
  status: OfflineStoreStatus;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

// --- affiliate networks -----------------------------------------------------

/**
 * First-class affiliate network (Awin, Impact, ...). Kept on a SEPARATE axis
 * from marketplaces (Deliveroo/Uber Eats/...), which are store_channels.channelType.
 * [EXTENSION — D-networks / D8]
 */
export interface AffiliateNetwork extends RaRecord<number> {
  name: string;
  slug: string;
  /** Query param carrying the click id, e.g. 'awc'. */
  clickParam: string;
  /** Query param used to build deeplinks, e.g. 'ued'. */
  deeplinkParam: string;
  /** Domain tracking links are built on, e.g. 'awin1.com'. */
  trackingDomain: string;
  lastImportedAt: string;
  /**
   * The network-account secret the IMPORT JOB authenticates with to pull this
   * network's data (D-settings-s2-networks). Per-network (a per-market credential
   * is a deferred refinement). OPAQUE and SENSITIVE: it is in SENSITIVE_AUDIT_FIELDS,
   * so a change records `[redacted]` in the audit diff (never the value), and the
   * edit form is write-only (it never reads the secret back). The real import job
   * reading it is the migration seam. null means no credential is set. [EXTENSION]
   */
  apiCredential: string | null;
  /**
   * DERIVED — number of merchant_regions on this network. Provider-injected
   * from merchantRegions; not stored in the seed.
   */
  storeCount?: number;
}

/** Per-market commission defaults for a network. [EXTENSION — D-networks] */
export interface AffiliateNetworkMarket extends RaRecord<number> {
  /** FK -> affiliateNetworks.id */
  networkId: number;
  market: Market;
  /** % default commission the network pays in this market (numeric(6,4), 4 dp). */
  defaultCommissionRate: number;
}

// --- users ------------------------------------------------------------------

export type UserTier = 'free' | 'plus';
export type SignupMethod = 'social' | 'email';

/**
 * Plus subscription lifecycle (D-subscriptions). IN-HOUSE vocabulary, NOT a
 * vendor mirror: Plus is a prepaid period funded from the member's wallet (a
 * fixed cashback amount per period), auto-renewed by debiting the wallet again.
 * There is no Stripe and no card billing anywhere in this model.
 *  - none: not a Plus member.
 *  - active: a paid period is running, auto-renew on.
 *  - pending_cancel: auto-renew turned off but still inside a paid period, so
 *    Plus persists until currentPeriodEnd.
 *  - lapsed: period ended after a chosen cancellation (auto-renew was off).
 *  - lapsed_insufficient: renewal failed because the wallet could not cover the
 *    period price. DISTINCT from lapsed: the recoverable group (opted in, just
 *    out of balance).
 */
export type PlusStatus =
  | 'none'
  | 'active'
  | 'pending_cancel'
  | 'lapsed'
  | 'lapsed_insufficient';

/**
 * Referral reward lifecycle (D-referrals). IN-HOUSE, engine-written: the admin
 * DISPLAYS this and can VOID, never pays. The referrer earns the reward only once
 * the referee spends a threshold (the anti-abuse gate), so a referral must NEVER
 * reach `qualified` without that spend genuinely met (the ENGINE enforces it; this
 * admin does not). This field is the seam where the referral engine writes back.
 *  - pending: referee referred and signed up, has NOT yet spent the threshold. No
 *    reward owed. Most fraudulent referrals stay here forever, by design.
 *  - qualified: referee crossed the spend threshold, reward owed, not yet paid.
 *  - paid: engine has conferred the reward into the referrer's wallet.
 *  - void: referral killed (fraud / abuse), reward never paid. Terminal.
 */
export type ReferralRewardStatus = 'pending' | 'qualified' | 'paid' | 'void';

/**
 * Account fraud / trust state (D-fraud-queue). IN-HOUSE and DELIBERATELY
 * EXTENSIBLE: more states (e.g. a watchlist or a review-hold) are expected to land
 * as the phase grows, so a later addition reads as planned, not a reshape. The
 * admin RECORDS the decision; ENFORCEMENT (blocking login) is consumer-side and not
 * built here (a cross-repo constraint, see D-fraud-queue).
 *  - active: normal account.
 *  - suspended: a TEMPORARY hold that carries a period (suspensionEndsAt),
 *    login-blocked while it runs, liftable by a fraud reviewer (reversible).
 *  - banned: TERMINAL, no period, login-blocked, reversible only by a deliberate
 *    super_admin reinstate (the most terminal write needs a second higher hand).
 */
export type FraudStatus = 'active' | 'suspended' | 'banned';

/**
 * Why an account was suspended or banned (D-suspension-flow). An IN-HOUSE,
 * DELIBERATELY EXTENSIBLE enum: the starting set covers the common fraud
 * categories and more codes are expected as the trust workstream grows. The
 * suspend AND the ban actions both capture a code (a ban needs a category too),
 * alongside an optional free-text note in `fraudReason`. It is the LIVE reason for
 * the current hold, so it is cleared to null on reinstate (an active user carries
 * no live reason; the history stays in the audit trail). Maps to the backbone's
 * `fraud_decisions.reason_code` at migration.
 *
 * Phase 6 (D-support-role) WIDENED this with SUPPORT-CONTEXT reasons (`member_request`,
 * `abusive_conduct`) so the SAME capability-guarded suspend serves the support agent
 * too. There is ONE reason field; the suspend action offers each ROLE the subset
 * appropriate to its context (the fraud subset vs the support subset, see
 * suspensionState), so a support suspension is recorded honestly and does not pollute
 * the fraud reason stats.
 */
export const SUSPENSION_REASON_CODES = [
  'payment_fraud',
  'referral_abuse',
  'identity_mismatch',
  'chargeback_pattern',
  'terms_violation',
  // Support-context (D-support-role).
  'member_request',
  'abusive_conduct',
] as const;
export type SuspensionReasonCode = (typeof SUSPENSION_REASON_CODES)[number];

/**
 * The appeal lifecycle on a suspended or banned account (D-suspension-flow).
 *  - none: no appeal raised (the default).
 *  - pending: the user has contested the decision; it surfaces on the worklist.
 *  - resolved: the appeal has been worked, by an OVERTURN (the existing reinstate /
 *    unban, which also sets active) or a DISMISS (the original decision stands).
 * MIDDLE scope: the resolution REUSES the existing reinstate / unban / dismiss
 * actions rather than a first-class appeal-review action (deferred until appeal
 * volume justifies it). The user's contest text (`appealNote`) is consumer-written
 * in real life, a cross-repo seam; the mock seeds it.
 */
export type AppealStatus = 'none' | 'pending' | 'resolved';

/**
 * A signal two accounts can share that suggests the same person is behind both
 * (D-ban-evasion). The identity signals (`deviceId` / `ipAddress` / `signupAddress`)
 * are engine-written stand-ins the real identity engine eventually writes, captured
 * consumer-side; `referral_cluster` is the ONE signal that already exists (the
 * referral self-FK). Detection matches on THESE, never on subscription or payout
 * artefacts (`billingReference` / `payoutDetails` are not identity, and matching on
 * them is the proxy-because-it-exists trap).
 */
export type LinkSignal =
  | 'deviceId'
  | 'ipAddress'
  | 'signupAddress'
  | 'referral_cluster';

/**
 * A recorded dismissal of a suspected ban-evasion link (D-ban-evasion). Stored on
 * the LINKED (active) account, keyed to the banned anchor and the signal set that
 * matched AT DISMISSAL. A link is suppressed only while its current matched set is
 * covered by a dismissal; a later STRONGER match (a new signal beyond the
 * dismissed-at set) RE-RAISES it, so a dismissal is never a permanent blind spot.
 */
export interface LinkDismissal {
  anchorId: number;
  signals: LinkSignal[];
}

/**
 * The backbone. `balance` is DERIVED, not stored. The fraud / trust fields
 * (`riskScore`, `fraudStatus`, `suspensionEndsAt`, `fraudReason`, `escalated`) were
 * held back from Phase 2 deliberately; Phase 5 (D-fraud-queue) has LIFTED that hold
 * and they now live here as the fraud workstream's home. `riskScore` is
 * engine-written (the admin displays and acts on it, never computes it), the same
 * write-back seam as promo usedCount / referral reward state.
 */
export interface User extends RaRecord<number> {
  name: string;
  email: string;
  market: Market;
  tier: UserTier;
  /** Old regType: how the user signed up. [EXTENSION — benign identity metadata] */
  signupMethod: SignupMethod;
  /**
   * True for users migrated from the OLD platform (Cashblack is rebuilding over a
   * live platform). The backbone has no legacy concept. [EXTENSION — D-legacy-users]
   */
  isLegacy: boolean;
  /**
   * Original signup date on the OLD platform, so "member since <year>" stays
   * truthful for migrated members. Null for users created on the new platform.
   * [EXTENSION — D-legacy-users]
   */
  legacySignupAt: string | null;
  /**
   * The user's OLD-platform id, kept to reconcile old transactions / history to
   * this user later. Null for new users. [EXTENSION — D-legacy-users]
   */
  legacyUserId: string | null;
  /**
   * Plus subscription state. The Subscriptions surface is a filtered VIEW over
   * users with plusStatus != none (D-subscriptions). The backbone keeps
   * subscription state on the user row too, so the real-data swap is a field
   * mapping, not a reshape. [EXTENSION — D-subscriptions, in-house not vendor]
   */
  plusStatus: PlusStatus;
  /**
   * Timestamp the current paid Plus period ends. In the future for active /
   * pending_cancel, in the past for a lapsed state, null for none.
   * [EXTENSION — D-subscriptions]
   */
  currentPeriodEnd: string | null;
  /**
   * Renewal state. On by default for a new Plus member (the consumer apps owe
   * the point-of-purchase disclosure; see D-subscriptions). Visible per row so
   * ops can answer a renewal dispute. [EXTENSION — D-subscriptions]
   */
  autoRenew: boolean;
  /**
   * Processor-NEUTRAL subscription reference (NOT a stripe_customer_id /
   * stripe_subscription_id; this platform does not use Stripe). The backbone's
   * stripe_* column names are the divergence to reconcile at migration; see
   * docs/schema-reconciliation.md. Null for a member who never subscribed.
   * [EXTENSION — D-subscriptions]
   */
  billingReference: string | null;
  /**
   * The referring user's id, or null for a user who was not referred. One-hop
   * self-FK mirroring the backbone's `users.referred_by_user_id`; the chain
   * derives by walking pointers. The Referrals explorer is a filtered view over
   * users with referredBy != null, keyed by the referrer (D-referrals).
   * [EXTENSION — D-referrals, backbone referred_by_user_id is the divergence]
   */
  referredBy: number | null;
  /**
   * Reward state for THIS user's one inbound referral (one-hop means each user has
   * exactly one referrer, so the referee row is the unambiguous home for that
   * edge's reward). Null when referredBy is null. Engine-written, admin-displayed.
   * [EXTENSION — D-referrals]
   */
  referralRewardStatus: ReferralRewardStatus | null;
  /** When the referral was made (the referee's referred signup). Null when not
   *  referred. [EXTENSION — D-referrals] */
  referredAt: string | null;
  /**
   * Risk score, the fraud queue's ranking dimension (D-fraud-queue). Scale 0 to 100
   * (higher = riskier) [REVIEW: RISK_SCALE]. STORED mock data the real risk engine
   * eventually writes; the admin displays and acts on it, NEVER computes it (the
   * write-back seam, like promo usedCount). [EXTENSION — D-fraud-queue]
   */
  riskScore: number;
  /** Account fraud / trust state (D-fraud-queue). The recorded decision a fraud
   *  action writes. [EXTENSION — D-fraud-queue] */
  fraudStatus: FraudStatus;
  /**
   * When a suspension ends (ISO). Set only while fraudStatus is `suspended`, null
   * otherwise. A NEW field, NOT the Plus currentPeriodEnd (which is subscription
   * state). [EXTENSION — D-fraud-queue]
   */
  suspensionEndsAt: string | null;
  /**
   * The recorded WHY for the current fraud status or escalation, written by the
   * action through the JustifiedAction reason capture so it lands in the audit
   * trail. Null when never actioned. [EXTENSION — D-fraud-queue]
   */
  fraudReason: string | null;
  /**
   * Escalation routing flag: a fraud reviewer has flagged the user for higher
   * review WITHOUT changing the status. [EXTENSION — D-fraud-queue]
   */
  escalated: boolean;
  /**
   * The structured category for the current suspension or ban (D-suspension-flow).
   * Captured on suspend / ban alongside the free-text `fraudReason`, cleared to null
   * on reinstate. Null when the account carries no live hold. [EXTENSION —
   * D-suspension-flow]
   */
  suspensionReasonCode: SuspensionReasonCode | null;
  /** The appeal lifecycle for the current hold (D-suspension-flow). Defaults to
   *  `none`. [EXTENSION — D-suspension-flow] */
  appealStatus: AppealStatus;
  /** When the appeal was raised (ISO), or null when there is none. [EXTENSION —
   *  D-suspension-flow] */
  appealedAt: string | null;
  /**
   * The user's contest text for a pending appeal. Consumer-app-written in real life
   * (the appeal-submission seam is cross-repo); the mock seeds it. Null when there
   * is no appeal. [EXTENSION — D-suspension-flow]
   */
  appealNote: string | null;
  /**
   * Device fingerprint stand-in (D-ban-evasion). Engine-written: the real identity
   * engine captures it consumer-side, the admin only DISPLAYS and acts on the
   * derived links. Null when unknown. [EXTENSION — D-ban-evasion]
   */
  deviceId: string | null;
  /** Last-known-IP stand-in (D-ban-evasion), engine-written consumer-side. Null
   *  when unknown. [EXTENSION — D-ban-evasion] */
  ipAddress: string | null;
  /** Physical-address stand-in (D-ban-evasion), engine-written consumer-side. Null
   *  when unknown. [EXTENSION — D-ban-evasion] */
  signupAddress: string | null;
  /**
   * Recorded dismissals of suspected ban-evasion links anchored on a banned account
   * (D-ban-evasion). A value-neutral recorded decision written through the
   * chokepoint and audited. Empty by default. [EXTENSION — D-ban-evasion]
   */
  linkDismissals: LinkDismissal[];
  createdAt: string;
  /**
   * DERIVED — confirmed cashback minus settled/in-flight cashouts minus chargeback
   * reversals, FLOORED at 0 (the member-facing figure). Provider-injected; not
   * stored.
   */
  balance?: number;
  /**
   * DERIVED — the same figure as `balance` but the TRUE SIGNED value, NOT floored
   * (D-chargebacks): a clawback that exceeds the remaining balance shows a genuine
   * negative here so the admin sees the real loss, while the member-facing `balance`
   * stays floored at 0. Provider-injected; not stored. [EXTENSION — D-chargebacks]
   */
  balanceSigned?: number;
}

// --- transactions (was cashbacks) ------------------------------------------

export type TransactionStatus =
  | 'pending'
  | 'confirmed'
  | 'waiting'
  | 'rejected'
  | 'cancelled';

/**
 * The MANUAL-ATTRIBUTION review lifecycle of a transaction (D-manual-attribution).
 * This is genuinely new: the old platform assumed transactions arrived
 * pre-attributed from the networks, but the rebuild can ingest a transaction with
 * NO confidently-known member (an orphan). This state is ORTHOGONAL to `status`
 * (pending / confirmed / ...): an orphan has both and they move independently. It is
 * also separate from `userId` (the member link itself).
 *  - unattributed: arrived with no known member, not yet worked. `userId` is null.
 *  - needs_review: flagged for a second look, still no member set.
 *  - attributed: a member has been assigned (`userId` set) by ops. Confirmation is
 *    STILL the normal cashbacks:resolve flow, so an attributed orphan can sit at
 *    status `pending` until confirmed (it does not yet count toward the balance).
 *  - rejected: examined and judged unattributable, will never count for anyone.
 * Normal (non-orphan) transactions are `attributed` by construction in the seed, so
 * they never enter the queue. The whole concept is a mock EXTENSION (the backbone
 * has no orphan / attribution-review model). [EXTENSION — D-manual-attribution]
 */
export type AttributionStatus =
  | 'unattributed'
  | 'needs_review'
  | 'attributed'
  | 'rejected';

/**
 * How the transaction was attributed — the backbone `attribution_method` enum
 * (verified against docs/backbone-schema.md). `auto_direct` = auto-attributed
 * on a direct integration, `auto_subnetwork` = auto via a sub-network,
 * `pending_integration` = awaiting an integration before it can be attributed.
 */
export type AttributionMethod =
  | 'auto_direct'
  | 'auto_subnetwork'
  | 'manual'
  | 'pending_integration';

/**
 * The CHARGEBACK / dispute lifecycle on a transaction (D-chargebacks). Genuinely
 * new: the old platform had no dispute model. ORTHOGONAL to `status` (the
 * attributionStatus precedent): a transaction can be `confirmed` AND `disputed` at
 * once, and they move independently. The original transaction STAYS confirmed even
 * when a dispute is lost (it genuinely was earned); the value reversal is a separate
 * ledger entry (a BalanceLedgerEntry), never a status flip.
 *  - none: no dispute (the default).
 *  - received: a dispute has been raised (by the member, the network, or the issuer).
 *  - under_review: the admin is working it, gathering the evidence note.
 *  - won: resolved in the platform's favour. No clawback, the cashback is kept.
 *  - lost: resolved against the platform. A reversal entry claws back the cashback.
 * [EXTENSION — D-chargebacks]
 */
export type DisputeStatus =
  | 'none'
  | 'received'
  | 'under_review'
  | 'won'
  | 'lost';

/** Who raised the dispute (D-chargebacks). [EXTENSION] */
export type DisputeSource = 'member' | 'network' | 'issuer';

/**
 * The chargeback reason category (D-chargebacks), an in-house extensible enum
 * reusing the suspension reason-code-plus-note pattern. Maps to the network /
 * issuer reason codes at migration. [EXTENSION]
 */
export const CHARGEBACK_REASON_CODES = [
  'fraud',
  'goods_not_received',
  'duplicate',
  'not_recognised',
  'other',
] as const;
export type ChargebackReasonCode = (typeof CHARGEBACK_REASON_CODES)[number];

/**
 * Manual balance adjustment reason codes (D-balance-adjustment). The structured WHY
 * for an admin-initiated wallet move, reusing the suspension / chargeback reason-
 * code-plus-note pattern. Extensible: add a code here and a label in
 * adjustmentsState. [EXTENSION]
 */
export const ADJUSTMENT_REASON_CODES = [
  'goodwill',
  'missed_cashback',
  'over_credit_correction',
  'erroneous_payment_recovery',
  'other',
] as const;
export type AdjustmentReasonCode = (typeof ADJUSTMENT_REASON_CODES)[number];

/**
 * Support-credit reason codes (D-support-role, the Phase 6 bounded credit). The
 * structured WHY for a support-agent credit, a positive-only specialisation of the
 * ledger entry. `refund` is an IN-PLATFORM credit, NOT a cashout reversal (reversing a
 * paid cashout is not a support action and stays at the super_admin / chargeback tier).
 * Extensible: add a code here and a label in ticketsState. [EXTENSION]
 */
export const SUPPORT_REASON_CODES = [
  'goodwill',
  'missed_cashback',
  'refund',
  'service_failure',
  'other',
] as const;
export type SupportReasonCode = (typeof SUPPORT_REASON_CODES)[number];

export interface Transaction extends RaRecord<number> {
  /**
   * FK -> users.id, or NULL for an ORPHAN: a transaction that arrived without a
   * confidently-known member, awaiting manual attribution (D-manual-attribution).
   * Widened from a required id by that surface. This stays the field `deriveBalance`
   * reads, so crediting an orphan (setting `userId`) is a pure LINKAGE write and the
   * value path is untouched: the balance derivation picks the transaction up later,
   * and only once it is confirmed. [EXTENSION — D-manual-attribution]
   */
  userId: number | null;
  /** FK -> merchants.id (backbone transactions.merchant_id). */
  merchantId: number;
  /** FK -> merchantRegions.id (backbone transactions.merchant_region_id; ties the txn to market). */
  merchantRegionId: number;
  /** FK -> clicks.id, or null (not all transactions are click-attributed). */
  clickId: number | null;
  /** Denormalised market mirror for uniform region scoping (real schema joins). */
  market: Market;
  /** Order value (backbone grossAmount). */
  grossAmount: number;
  /** Member cashback (old memberShare; backbone cashbackAmount). */
  cashbackAmount: number;
  /** Network -> platform actual earned on this order (old commission). [EXTENSION — D-commission] */
  commissionAmount: number;
  currency: Currency;
  status: TransactionStatus;
  /**
   * Conversion time — when the retailer reports the sale happened (backbone
   * transactions.occurred_at). NOT the click time and NOT row-creation; this is
   * the ops-relevant date for support queries.
   */
  occurredAt: string;
  /**
   * When the transaction was confirmed, or null while not yet confirmed
   * (backbone transactions.confirmed_at). Set for `confirmed` rows.
   */
  confirmedAt: string | null;
  /**
   * Projected confirmation date for in-flight (`pending` / `waiting`)
   * transactions, or null once resolved (backbone
   * transactions.expected_confirmation_at).
   */
  expectedConfirmationAt: string | null;
  /** Ingestion / row-creation time, just after occurredAt (backbone transactions.created_at). */
  createdAt: string;
  /**
   * Manual-attribution review state (D-manual-attribution). Optional: present on
   * orphans and on transactions worked through the queue; normal transactions are
   * seeded `attributed`. Orthogonal to `status`. [EXTENSION — D-manual-attribution]
   */
  attributionStatus?: AttributionStatus;
  /**
   * Match BASIS, captured at a CREDIT action: the candidate click ops matched the
   * orphan to (so `clicks[matchedClickId].userId` is the member credited), or null
   * when credited without a click. Rides alongside the linkage write so it lands in
   * the audit trail. A record of what ops acted on, NOT a validated match.
   * [EXTENSION — D-manual-attribution]
   */
  matchedClickId?: number | null;
  /**
   * Match BASIS as free text, captured at a CREDIT (clickless) or REJECT action:
   * the justification for crediting without a click, or for rejecting as
   * unattributable. [EXTENSION — D-manual-attribution]
   */
  attributionNote?: string | null;
  /**
   * The chargeback / dispute lifecycle state (D-chargebacks). Optional and ORTHOGONAL
   * to `status`: absent / `none` means no dispute. A `confirmed` transaction can be
   * disputed and stays confirmed throughout (a lost dispute writes a separate
   * reversal entry, never flips this). [EXTENSION — D-chargebacks]
   */
  disputeStatus?: DisputeStatus;
  /** Who raised the dispute (D-chargebacks). Null when there is none. [EXTENSION] */
  disputeSource?: DisputeSource | null;
  /** The structured chargeback reason (D-chargebacks). Null when there is none.
   *  [EXTENSION] */
  disputeReasonCode?: ChargebackReasonCode | null;
  /** The evidence note for the dispute (what we have or are submitting), captured
   *  via JustifiedAction. NOT a document subsystem. [EXTENSION — D-chargebacks] */
  disputeNote?: string | null;
  /** When the dispute was resolved (won or lost), or null while open. [EXTENSION] */
  resolvedAt?: string | null;
}

/**
 * What kind of ledger entry moved value. A `chargeback_reversal` is a clawback tied
 * to a disputed transaction (always a debit, D-chargebacks); a `manual_adjustment` is
 * an unbounded admin-initiated wallet move (a credit or a debit, D-balance-adjustment);
 * a `support_credit` is a bounded, POSITIVE-ONLY support-agent credit (D-support-role,
 * the Phase 6 specialisation of the same ledger mechanism with a lower cap and a
 * support capability).
 */
export type LedgerEntryKind =
  | 'chargeback_reversal'
  | 'manual_adjustment'
  | 'support_credit';

/**
 * A signed BALANCE LEDGER entry (D-balance-adjustment) — the ONE shared ledger both
 * the chargeback clawback and the manual balance adjustment write. The balance is
 * DERIVED from these entries (deriveBalanceSigned sums them), NEVER a direct balance
 * write, so value only ever moves through a recorded entry. `amount` is SIGNED:
 * negative debits the wallet (a chargeback reversal is always negative, and so is a
 * manual debit), positive credits it (a manual credit). `transactionId` ties a
 * chargeback reversal to its disputed order (a PARTIAL reversal is allowed) and is
 * null for a manual adjustment. Generalised from the Phase 5 chargeback reversal so
 * manual balance adjustment inherits the exact mechanism. The backbone ledger is the
 * real-data target. [EXTENSION — D-balance-adjustment, D-chargebacks]
 */
export interface BalanceLedgerEntry extends RaRecord<number> {
  userId: number;
  /** The disputed order for a chargeback reversal; null for a manual adjustment. */
  transactionId: number | null;
  /** SIGNED: negative debits the wallet, positive credits it. */
  amount: number;
  market: Market;
  kind: LedgerEntryKind;
  /** The structured why: a chargeback reason for a reversal, an adjustment reason
   *  for a manual adjustment, a support reason for a support credit. */
  reasonCode: ChargebackReasonCode | AdjustmentReasonCode | SupportReasonCode;
  /** The recorded free-text why, captured via the action so it lands in the audit. */
  reason: string;
  createdAt: string;
}

/*
 * Note: the backbone transaction carries NO store_channel_id and NO
 * attribution_method. Attribution (and the effective rate) is snapshotted on the
 * originating click (see Click.attributionMethod / rateSnapshot), not here.
 */

// --- cashout requests (was cashouts) ---------------------------------------

/** Where the money goes — orthogonal to how it physically moves (D6). */
export type CashoutDestination = 'payout' | 'donation';

/** Market-specific payout rails (backbone payoutMethod). Donations use none. */
export type PayoutMethod =
  | 'uk_bank'
  | 'uk_paypal'
  | 'us_ach'
  | 'us_paypal'
  | 'us_venmo';

/**
 * Progress status. The backbone's member-meaningful values plus `approved`
 * (DEPRECATED, see below). Manual execution at launch (D-cashouts-manual-execution,
 * the web repo's ADR 0005): the admin moves a cashout `requested → processing`, a
 * human operator sends the money by hand and marks it `processing → paid | failed`.
 * "Donated" is NOT a status (it is the derived `destinationType==='donation' &&
 * status==='paid'`).
 *
 * `approved` is DEPRECATED. It was the admin→engine hand-off state from the original
 * PayoutEngine design (D-payout-boundary): the admin set `approved` and a future
 * engine consumed it. The web repo's ADR 0005 makes execution MANUAL at launch (there
 * is no engine yet), so approve now targets `processing` directly and nothing sets
 * `approved` any more. It stays in the enum so historical rows and the migration can
 * reconcile the enums; the automated engine is deferred to the cross-repo PayoutEngine.
 */
export type CashoutStatus =
  | 'requested'
  // DEPRECATED (D-cashouts-manual-execution): the old admin->engine hand-off state.
  // No admin action sets it now (approve targets `processing`); kept for history.
  | 'approved'
  | 'processing'
  | 'paid'
  | 'failed'
  | 'rejected'
  | 'cancelled';

/**
 * The clean payout-vs-donation split (D6). `destinationType` (where it goes) is
 * orthogonal to `status` (how far along). A payout sets `payoutMethod`; a
 * donation sets `charityId`. The old 'donated' status is preserved as a DERIVED
 * display state: destinationType === 'donation' && status === 'paid'.
 */
export interface CashoutRequest extends RaRecord<number> {
  /** FK -> users.id */
  userId: number;
  amount: number;
  currency: Currency;
  /** Denormalised market mirror for uniform region scoping (real schema joins). */
  market: Market;
  destinationType: CashoutDestination;
  /** Rail — set iff destinationType === 'payout', else null. */
  payoutMethod: PayoutMethod | null;
  /** FK -> charities.id — set iff destinationType === 'donation', else null. [EXTENSION — D4] */
  charityId: number | null;
  /**
   * Opaque encrypted envelope (backbone cashout_requests.payout_details — text,
   * a "v1:<base64>" string, NOT structured JSON). Set for payouts; null for
   * donations (a mock extension that carries no rail details).
   */
  payoutDetails: string | null;
  status: CashoutStatus;
  /** SLA target for settlement (backbone slaTargetAt). */
  slaTargetAt: string;
  /** When the SLA was breached, or null (backbone slaBreachedAt). */
  slaBreachedAt: string | null;
  requestedAt: string;
  /** When the cashout was paid, or null (backbone cashout_requests.paid_at — there is no completed_at). */
  paidAt: string | null;
  /**
   * The member-renderable reason a payout failed, or null (backbone
   * cashout_requests.failed_reason). Set by the admin's mark-failed action (a forced
   * reason, D-cashouts-manual-execution); the member wallet renders it. Null unless
   * status === 'failed'.
   */
  failedReason: string | null;
}

// --- welcome-bonus funding (platform-level) --------------------------------

/**
 * One welcome-bonus funding record — the platform paying a new member their
 * welcome bonus. This is a PLATFORM cost, funded at the member's qualifying
 * withdrawal, and it NEVER touches any merchant's or network's margin: the
 * Earnings reconciliation subtracts the per-market welcome total from confirmed
 * margin to reach contribution, but no entity row ever absorbs a penny of it.
 *
 * The backbone funds the welcome stack through the member ledger / cashout path
 * (the `events` table records the `welcome_stack_n` / `first_payout` milestones
 * but carries no funding AMOUNT, and there is no welcome-bonus ledger table), so
 * this explicit per-funding record is a mock addition. [EXTENSION - D-earnings-welcome]
 */
export interface WelcomeBonusFunding extends RaRecord<number> {
  /** FK -> users.id (the member whose welcome bonus was funded). */
  userId: number;
  market: Market;
  currency: Currency;
  /** Bonus amount funded to the member, in the market's currency. */
  amount: number;
  /** When the bonus was funded — at the member's qualifying withdrawal (ISO). */
  fundedAt: string;
}

// --- charities (first-class) ------------------------------------------------

/** User-managed charity-type taxonomy (lookup table, not an enum). [EXTENSION — D4] */
export interface CharityType extends RaRecord<number> {
  key: string;
  label: string;
  description: string;
}

export type CharityStatus = 'active' | 'inactive';

/** Region-scoped charity (web roadmap P11). [EXTENSION — D4] */
export interface Charity extends RaRecord<number> {
  name: string;
  /** FK -> charityTypes.id */
  charityTypeId: number;
  description: string;
  logoUrl: string;
  bannerUrl: string;
  market: Market;
  status: CharityStatus;
  createdAt: string;
}

// --- categories (first-class, hierarchical) --------------------------------

export type CategoryStatus = 'active' | 'inactive';

/**
 * Hierarchical category tree, superseding the flat merchant_category enum (D3).
 * Shared across markets (no market field); revisit if categories go per-market.
 * [EXTENSION — D3]
 */
export interface Category extends RaRecord<number> {
  name: string;
  slug: string;
  /** Parent category id, or null for a top-level category. */
  parentId: number | null;
  /** Coarse rollup to the flat enum value (migration source). */
  coarseCategory: MerchantCategory;
  displayOrder: number;
  status: CategoryStatus;
  createdAt: string;
}

// --- audit log (first-class; the audit floor) -------------------------------

/** The mutating actions an audit entry records. Reads are not audited. */
export type AuditAction = 'create' | 'update' | 'delete';

/**
 * One audit entry — a single mutation through the data provider, recorded
 * automatically by the provider wrapper (see `./withAudit.ts`, DECISIONS.md
 * D-audit). Every create / update / delete the panel performs produces one of
 * these from the very first write, so the trail can never be skipped by a
 * surface forgetting to log.
 *
 * `before` / `after` hold ONLY the changed fields (a one-field edit is a
 * one-field diff), never whole records. Sensitive opaque fields
 * (payoutDetails-class) are NEVER snapshotted by value: if such a field changes,
 * its NAME is recorded with a redacted marker, not its value (see
 * `REDACTED_MARKER`). [EXTENSION — D-audit] The real backbone has no audit table;
 * the eventual admin API must implement the same behaviour server-side (the
 * client-side wrapper is interim, the API is the real enforcement point).
 */
export interface AuditLogEntry extends RaRecord<number> {
  /** The acting admin's identity id (Cognito `sub`), from getIdentity. */
  actorId: string;
  /** The acting admin's email, from getIdentity. */
  actorEmail: string;
  /** The acting admin's `custom:role` at the time of the action, or null. */
  actorRole: string | null;
  action: AuditAction;
  /** The resource name mutated, e.g. 'merchants'. */
  resource: string;
  /** The id of the mutated record. */
  recordId: number | string;
  /** Changed fields' previous values (null for create). */
  before: Record<string, unknown> | null;
  /** Changed fields' new values (null for delete). */
  after: Record<string, unknown> | null;
  /** The admin's active region context at the time, or null. */
  region: string | null;
  occurredAt: string;
}

/** An audit entry before the store assigns its id. */
export type AuditEntryDraft = Omit<AuditLogEntry, 'id'>;

/** Marker recorded in place of a sensitive field's value (never the value). */
export const REDACTED_MARKER = '[redacted]';

/**
 * Field names whose VALUES must never enter an audit snapshot — opaque /
 * encrypted envelopes (payoutDetails-class). When one changes, the audit records
 * the field NAME with `REDACTED_MARKER`, proving a change happened without
 * leaking the value. Grow this set as new sensitive fields appear.
 */
export const SENSITIVE_AUDIT_FIELDS: ReadonlySet<string> = new Set([
  'payoutDetails',
  // The affiliate-network account secret (D-settings-s2-networks). A credential
  // change records [redacted] in the audit, proving the change without leaking it.
  'apiCredential',
  // The Slack incoming-webhook URL (D-internal-alerts). A bearer secret (anyone with it
  // can post to the channel): a change records [redacted], never the value.
  'slackWebhookUrl',
]);

/**
 * The audit write seam. The mock provider owns the audit store and exposes this
 * so the provider wrapper can append entries; the audit List reads them back
 * through the normal `getList('audit')` path. When the real API audits
 * server-side, the wrapper's write-interception becomes a no-op and the same
 * `getList('audit')` reads the server's trail (D-audit).
 */
export interface AuditSink {
  appendAuditEntries: (entries: readonly AuditEntryDraft[]) => void;
}

// --- clicks (data only — no surface yet) ------------------------------------

/**
 * Click data. Not an admin surface yet (Phase 3), but seeded so transaction
 * `clickId`s resolve and merchant/region click counts can be DERIVED rather
 * than stored.
 *
 * Attribution and the effective rate are snapshotted HERE (backbone
 * clicks.attribution_method / rate_snapshot), not on the transaction.
 */
export interface Click extends RaRecord<number> {
  /** FK -> users.id */
  userId: number;
  /** FK -> merchants.id (backbone clicks.merchant_id). */
  merchantId: number;
  /** FK -> merchantRegions.id */
  merchantRegionId: number;
  /** Denormalised market mirror (= user.market = region.market). */
  market: Market;
  /** Attribution snapshotted at click time (backbone clicks.attribution_method). */
  attributionMethod: AttributionMethod;
  /** Effective cashback rate at click time (backbone clicks.rate_snapshot, numeric(6,4), 4 dp). */
  rateSnapshot: number;
  /**
   * The real click moment (the backbone's clicks.occurred_at). The store
   * performance maturation cutoff measures click age from THIS, not from
   * createdAt (row-insert time). Seeded to the same value the click uses for age
   * so existing behaviour is unperturbed; the real-data swap maps it straight to
   * clicks.occurred_at with no logic change. [EXTENSION - D-store-performance]
   */
  occurredAt: string;
  createdAt: string;
  /** True when the click produced a transaction. */
  converted: boolean;
}

// --- promo codes ------------------------------------------------------------

/**
 * What a promo code confers on redemption (D-promo-codes). A proper enum shipped
 * with ONE member, so adding types later (percentage, free_plus_period) is an
 * additive value change, not a schema change. Those wait for the engine that
 * honours them; today the only effect is a fixed wallet credit.
 */
export type PromoEffectType = 'fixed_wallet_credit';

/**
 * A promo code DEFINITION (D-promo-codes). Built from scratch: the backbone has
 * only a dangling FK-less `users.signup_promo_code_id` and no promo_codes table,
 * noted as the migration divergence in docs/schema-reconciliation.md. [EXTENSION]
 *
 * This is a catalogue entry, NOT a redemption: redemption is not modelled here
 * (no engine, no redemption record, no code-to-user link). The admin defines
 * codes; a future engine honours them and writes `usedCount` back. `usedCount` is
 * a DISPLAYED number this surface never increments.
 */
export interface PromoCode extends RaRecord<number> {
  /** The code string. Unique within a market, uppercase convention. */
  code: string;
  /** One market per code, always (D-promo-codes). No global / market-agnostic code. */
  market: Market;
  effectType: PromoEffectType;
  /** The grant amount, in the market's currency (currency implied by market). */
  effectValue: number;
  /** Validity window (ISO timestamps). */
  validFrom: string;
  validTo: string;
  /** Redemption cap. Null = uncapped. */
  maxRedemptions: number | null;
  /** Redemptions so far. DISPLAYED only; the future engine writes it back. */
  usedCount: number;
  /** Soft-disable flag (deactivate-not-delete, like charities). */
  active: boolean;
  createdAt: string;
}

// --- placements -------------------------------------------------------------

/**
 * The fixed promotional slots a placement can occupy — the backbone
 * `placement_slot` enum (verified against docs/backbone-schema.md). A CLOSED set,
 * modelled as an enum field, NOT a separate slots-inventory table: the backbone
 * carries no per-slot capacity, so there is nothing to model beyond the enum
 * (D-placements).
 */
export type PlacementSlot =
  | 'homepage_hero'
  | 'app_push_target'
  | 'email_feature';

/**
 * A merchant occupying a promotional SLOT in a market for a time WINDOW with a
 * WEIGHT (D-placements). Built from scratch: the backbone defines
 * `merchant_placements` (slot enum, priority weight, window, region) but the mock
 * never modelled it; this type is the migration target, noted as the divergence in
 * docs/schema-reconciliation.md. [EXTENSION — D-placements]
 *
 * INTERNAL MERCHANDISING, not sold inventory: there is deliberately NO price
 * field. The platform chooses which brand to feature; no money is attached.
 * Multiple placements may legitimately share a slot+window — the weight is the
 * share-of-voice arbitration, so overlap is NORMAL not an error and is surfaced,
 * never blocked. Live-ness is DERIVED purely from the window (no stored flag), and
 * stopping a placement is ENDING its window: there is no `active` flag and no
 * soft-disable (a divergence from charities / promo codes, recorded in
 * D-placements).
 */
export interface Placement extends RaRecord<number> {
  /**
   * FK -> merchants.id (the BRAND, mirroring the backbone
   * merchant_placements.merchant_id; NOT a merchant_region).
   */
  merchantId: number;
  /** The slot occupied (the fixed backbone placement_slot enum). */
  slot: PlacementSlot;
  /**
   * One market per placement (the mock `market` convention). The backbone column
   * is named `region` (a merchant_region enum, uk/us) — a NAMING divergence to
   * reconcile, same value space. No global / both-market placement (a both-market
   * booking is two rows).
   */
  market: Market;
  /**
   * Share-of-voice arbitration value (the backbone `priority`, integer, default
   * 0). When placements share a slot+window, each competitor's share of voice is
   * its weight over the competing total; higher weight wins more impressions.
   */
  weight: number;
  /** Window start (ISO; backbone starts_at, not null). */
  startsAt: string;
  /** Window end (ISO), or null for open-ended (backbone ends_at, nullable). */
  endsAt: string | null;
  createdAt: string;
}

// --- support tickets (Phase 6) ---------------------------------------------

/** The ticket lifecycle (D-ticket-inbox). `escalated` routes to a higher hand
 *  (e.g. fraud or super_admin); the others are the support agent's own states. */
export type TicketStatus = 'open' | 'pending' | 'resolved' | 'escalated';

/** Who opened the ticket (D-ticket-inbox). `user_initiated` is a member raising it;
 *  `system_generated` is the platform raising it (a failed payout, a fraud flag),
 *  carrying a triggering-entity link. */
export type TicketSource = 'user_initiated' | 'system_generated';

/** The entity that triggered a system-generated ticket (D-ticket-inbox), mirroring
 *  the BalanceLedgerEntry.transactionId link. Null for a user-initiated ticket. */
export interface TicketTrigger {
  kind: 'transaction' | 'cashout';
  id: number;
}

/**
 * A support ticket (D-ticket-inbox, the Phase 6 lead surface). A FIRST-CLASS entity
 * (unlike a dispute, which rides on a transaction): the inbox is a worklist over this
 * collection. A ticket is about a member (`userId`) in a single `market`. The SLA
 * BREACH is DERIVED off the injected clock (now past `slaTargetAt` and not resolved),
 * NOT a stored flag (the suspensions elapsed-derivation pattern). Actions (assign,
 * resolve, escalate) route through the chokepoint and are audited. [EXTENSION]
 */
export interface Ticket extends RaRecord<number> {
  /** FK -> users.id (the member the ticket is about). */
  userId: number;
  /** Denormalised market mirror for uniform region scoping (the member's market). */
  market: Market;
  status: TicketStatus;
  /** The agent the ticket is assigned to, or null when unassigned. */
  assignee: string | null;
  source: TicketSource;
  /** The triggering entity for a system-generated ticket, or null. */
  triggeringEntity: TicketTrigger | null;
  subject: string;
  body: string;
  /** The SLA due time. A BREACH is derived (now > slaTargetAt and not resolved). */
  slaTargetAt: string;
  createdAt: string;
}

// --- canned responses + macros (Phase 6) -----------------------------------

/** What a canned response is (D-canned-responses). A `response` is plain reusable
 *  text the agent renders and copies out; a `macro` is the same text PLUS a bundled
 *  ticket action (the action half goes through the capability-guarded chokepoint, so a
 *  macro can never do more than the acting agent already holds). */
export type CannedResponseKind = 'response' | 'macro';

/**
 * The bundled action of a MACRO (D-canned-responses). v1 macros bundle a ticket STATUS
 * TRANSITION (resolve or escalate), the two value-neutral lifecycle moves the support
 * agent already drives. The action is fired through `performStatusTransition` (the same
 * chokepoint the inbox buttons use), so it is capability-GUARDED regardless of the macro:
 * an agent without the capability gets the rendered text but the transition is rejected.
 * `assign` is deliberately excluded (it writes an assignee, not a status, and is trivially
 * available as its own affordance); crediting and suspending stay their own deliberate
 * actions, never a macro side effect.
 */
export interface CannedMacroAction {
  /** The capability the bundled transition needs, guarded at the chokepoint. */
  capability: 'tickets:resolve' | 'tickets:escalate';
  /** The ticket status the bundled transition writes. */
  toStatus: 'resolved' | 'escalated';
}

/**
 * A canned response template (D-canned-responses, the Phase 6 surface-2 library). A
 * SHARED GLOBAL library (no per-agent ownership, no market tag in v1: uk/us copy share),
 * managed as Charities-shaped CRUD (create / edit / RETIRE via the active flag, never a
 * hard delete) and USED from a ticket context where the body is rendered with the member's
 * values substituted and COPIED OUT. The library NEVER writes into a ticket (there is no
 * reply/thread model; that is a deferred surface). The backbone has no template table; this
 * is the migration target. [EXTENSION — D-canned-responses]
 */
export interface CannedResponse extends RaRecord<number> {
  /** The agent-facing label, e.g. "Missing cashback explainer". */
  name: string;
  /** The template text, carrying `{member_name}`-style substitution placeholders. */
  body: string;
  kind: CannedResponseKind;
  /** The bundled ticket action for a macro; null for a plain response. */
  action: CannedMacroAction | null;
  /** Soft-disable: a retired template stays referenceable but leaves the use library. */
  active: boolean;
  createdAt: string;
}

// --- impersonation sessions (Phase 6) --------------------------------------

/**
 * A recorded VIEW-ONLY impersonation session (D-impersonation, the Phase 6 surface-4
 * close). The accountability spine of "view as user": who viewed whom, when, and for how
 * long. It is the ONLY thing the impersonation surface writes. There is NO cross-app auth
 * switch (the auth model is the admin actor only); "view as" is an admin-side member's-eye
 * RECONSTRUCTION wrapped in this session, never logging in as the member.
 *
 * The actor (the impersonating admin) is NOT stored here: the audit floor records it
 * automatically on the session create / update, so the row stays the subject side. A
 * session START is a create (audited), an EXPLICIT END is an update setting endedAt
 * (audited). EXPIRY is DERIVED off the clock (now past expiresAt), NEVER written and never
 * read-triggering a write: the start entry plus expiresAt bound the exposure window.
 * ACTIVE is derived: endedAt null and now before expiresAt. The real backend's
 * impersonation / session system is the eventual home. [EXTENSION]
 */
export interface ImpersonationSession extends RaRecord<number> {
  /** FK -> users.id: the member being viewed. */
  subjectUserId: number;
  /** Denormalised mirror of the subject's market, for region scoping. */
  market: Market;
  startedAt: string;
  /** Set at create from a fixed duration; the session auto-expires at this instant. */
  expiresAt: string;
  /** When the agent explicitly ended it, or null (still open, subject to expiry). */
  endedAt: string | null;
}

// --- DSAR requests (Phase 7) -----------------------------------------------

/**
 * The DSAR lifecycle (D-dsar, the Phase 7 lead). A DSAR (data-subject access request) is a
 * member asking for everything the platform holds on them, fulfilled by a compliance officer
 * within a STATUTORY deadline. The vocabulary is its OWN, NOT a ticket status: a DSAR is a
 * regulated request, not a support ticket.
 *  - received: lodged, the statutory clock has started, not yet worked.
 *  - in_progress: a compliance officer has taken it up (dsar:process).
 *  - fulfilled: the member's data package has been assembled and produced (dsar:fulfil). Terminal.
 *  - rejected: refused as invalid or unverified, with a recorded reason (reject). Terminal.
 */
export type DsarStatus = 'received' | 'in_progress' | 'fulfilled' | 'rejected';

/**
 * A data-subject access request (D-dsar, the Phase 7 lead surface). A FIRST-CLASS entity (the
 * ticket precedent, NOT an overload of Ticket): a regulated request with a statutory deadline
 * and a compliance owner. The statutory DEADLINE is DERIVED off the injected clock
 * (requestedAt plus a fixed window, the ticket-SLA / suspension-elapse pattern), NOT a stored
 * flag and NO enforcement engine. The handling actions (process, fulfil, reject) route through
 * the chokepoint and are audited; the acting compliance officer is recorded by the audit floor,
 * NOT stored on the row. The real backend's data-subject-request system is the eventual home.
 * [EXTENSION]
 */
export interface DsarRequest extends RaRecord<number> {
  /** FK -> users.id: the member whose data the request is for. */
  subjectUserId: number;
  /** Denormalised mirror of the subject's market, for the region facet. */
  market: Market;
  status: DsarStatus;
  /** When the request was lodged: the statutory clock starts here. */
  requestedAt: string;
  /** When the package was produced and the request marked fulfilled, or null. */
  fulfilledAt: string | null;
  /** When the request was rejected, or null. */
  rejectedAt: string | null;
  /**
   * The recorded WHY for a rejection, captured via the action so it lands in the audit
   * trail. MANDATORY at rejection (refusing a statutory request must be defensible); null
   * for a request that has not been rejected.
   */
  rejectionReason: string | null;
}

// --- consent records (Phase 7) ---------------------------------------------

/**
 * The consent types the platform tracks (D-consent-records, Phase 7 surface 2). An IN-HOUSE,
 * DELIBERATELY EXTENSIBLE enum (the SUSPENSION_REASON_CODES / CHARGEBACK_REASON_CODES idiom):
 * the starting set covers the common data-protection consents and more are expected as the
 * privacy workstream grows. The backbone's only consent-shaped table is the lossy current-state
 * `user_notification_preferences.prefs` jsonb blob with no enumerated keys, so this vocabulary
 * is unconfirmed against it and additive by design. [EXTENSION]
 */
export const CONSENT_TYPES = [
  'marketing_email',
  'marketing_push',
  'data_processing',
  'cookies',
  'third_party_sharing',
  'age_confirmation',
] as const;
export type ConsentType = (typeof CONSENT_TYPES)[number];

/** What a consent record records: a GRANT or a REVOKE of a consent type. */
export type ConsentAction = 'grant' | 'revoke';

/**
 * Where a consent record came from (the provenance). `signup` and `settings` are consumer-app
 * captures; `consumer_app` is any other app-driven change; `compliance_logged` is the ONE
 * admin-written source (a withdrawal received off-app, via phone or email, that compliance must
 * honour and record, the one narrow consent:record_withdrawal write).
 */
export type ConsentSource =
  | 'signup'
  | 'settings'
  | 'consumer_app'
  | 'compliance_logged';

/**
 * One APPEND-ONLY consent record (D-consent-records, Phase 7 surface 2). Consent is modelled as
 * an immutable HISTORY of grants and revocations, NEVER a current-state flag: the standing
 * consent for a type is DERIVED on read as the LATEST record (by occurredAt) for that type (the
 * balance-ledger / deriveBalanceSigned pattern), so it can never drift from the trail. The
 * member (the consumer app) writes grants and app-driven revocations in real life (the
 * engine-written / consumer-written seam, like riskScore and appealNote); the mock seeds the
 * history. The ONE admin write is consent:record_withdrawal, which appends a `revoke` sourced
 * `compliance_logged`.
 *
 * THE MOCK LEADS THE BACKBONE here: the real `user_notification_preferences` is a lossy
 * current-state jsonb blob that cannot prove WHAT permission existed WHEN, so it must grow into
 * this history shape for the consent record to be legally provable (see
 * docs/schema-reconciliation.md). [EXTENSION]
 */
export interface ConsentRecord extends RaRecord<number> {
  /** FK -> users.id: the member the consent is about. */
  userId: number;
  /** Denormalised mirror of the member's market, for the region facet. */
  market: Market;
  consentType: ConsentType;
  action: ConsentAction;
  source: ConsentSource;
  /**
   * The privacy-policy version this record was made against. A SEEDED stand-in: the real
   * version registry is a cross-repo seam (the riskScore engine-written-placeholder pattern).
   * The surface shows the version IDENTIFIER, never the policy TEXT (no overclaim).
   */
  policyVersion: string;
  /**
   * Free text for a `compliance_logged` withdrawal: how the withdrawal was received. Null for a
   * consumer-app record.
   */
  note: string | null;
  occurredAt: string;
}

// --- sanctions screening (Phase 7) -----------------------------------------

/**
 * The sanctions list a match came from (D-sanctions, Phase 7 surface 3). An IN-HOUSE,
 * DELIBERATELY EXTENSIBLE enum (the reason-code idiom): `ofac` is the US Treasury OFAC list,
 * `uk_hmt` the UK HMT consolidated list, `pep` a politically-exposed-person match. The value
 * indicates the JURISDICTION / source. More lists are expected as the screening programme grows.
 * [EXTENSION]
 */
export const SANCTIONS_LISTS = ['ofac', 'uk_hmt', 'pep'] as const;
export type SanctionsList = (typeof SANCTIONS_LISTS)[number];

/**
 * The review lifecycle of a sanctions match (D-sanctions).
 *  - pending_review: the engine produced a potential match, awaiting compliance review.
 *  - cleared: reviewed and judged a FALSE POSITIVE (a name collision, the common case). Terminal.
 *  - confirmed: reviewed and judged a TRUE MATCH, a legal determination that the platform must
 *    not transact with this person. Terminal. The downstream enforcement (login block, payout
 *    hold) is a cross-repo seam; this status is the recorded determination, not the execution.
 */
export const SANCTIONS_STATUSES = [
  'pending_review',
  'cleared',
  'confirmed',
] as const;
export type SanctionsStatus = (typeof SANCTIONS_STATUSES)[number];

/**
 * One potential sanctions match (D-sanctions, Phase 7 surface 3). ENGINE-WRITTEN: a real
 * screening engine screens members against the sanctions lists consumer/backend-side and writes
 * the matches; the admin REVIEWS them and NEVER computes them (the riskScore / ban-evasion-signals
 * stand-in pattern). There is no real list and no admin "run a screen" action: screening is the
 * engine's job, a cross-repo seam. The match is anchored on the MEMBER (`userId`); the payout-hold
 * for a confirmed match is a downstream consequence, not a second match stream.
 *
 * The user's SANCTIONS STANDING is DERIVED, not a stored duplicate flag on the user: a member with
 * an active `confirmed` match is sanctions-flagged (the derived-balance / derived-consent pattern,
 * avoiding two fields that drift). Confirming records `sanctionsStatus = 'confirmed'` here and does
 * NOT flip `fraudStatus` (a sanctions freeze is a different determination from a fraud ban). The
 * real screening system is the eventual backend home. [EXTENSION]
 */
export interface SanctionsMatch extends RaRecord<number> {
  /** FK -> users.id: the screened member the match is anchored on. */
  userId: number;
  /** Denormalised mirror of the member's market, for the region facet. */
  market: Market;
  /** Which list the match came from (the jurisdiction / source). Engine-written. */
  matchedList: SanctionsList;
  /**
   * The engine's match confidence, 0 to 100 (higher = stronger) [REVIEW: scale]. The
   * riskScore-shaped ranking dimension the queue sorts by. Engine-written, the admin never sets it.
   */
  matchScore: number;
  /** The sanctioned name the member resembles (the name-collision evidence). Engine-written. */
  matchedName: string;
  /** When the engine produced the match. */
  screenedAt: string;
  /** The review lifecycle state. The clear / confirm actions write this through the chokepoint. */
  sanctionsStatus: SanctionsStatus;
  /**
   * The recorded WHY for the review decision, captured via JustifiedAction so it lands in the
   * audit trail. MANDATORY on both clear and confirm; null while pending. [EXTENSION]
   */
  reviewReason: string | null;
  /** When the match was cleared or confirmed, or null while pending. */
  reviewedAt: string | null;
}

// --- erasure requests (Phase 7) --------------------------------------------

/**
 * The erasure (right-to-be-forgotten) lifecycle (D-erasure, Phase 7 surface 6, the last and
 * sharpest). Erasure is the most IRREVERSIBLE action in the build, so the lifecycle is built around a
 * GRACE WINDOW and a maker-checker:
 *  - requested: the request is logged and the GRACE WINDOW runs (requestedAt plus a fixed window,
 *    derived off the injected clock, the DSAR-statutory-deadline / suspension-elapse pattern). During
 *    grace the request is CANCELLABLE (a mistake or a withdrawn request caught before execution).
 *  - executed: TERMINAL and IRREVERSIBLE. Gated BOTH on the grace window having ELAPSED AND on
 *    super_admin (the second higher hand). On execute the mock performs a minimal owned-row TOMBSTONE
 *    (scrub the User PII, sever the two nullable links); the real cascade-delete-and-anonymise across
 *    every member table is the BACKEND seam (the authorise-vs-execute boundary).
 *  - cancelled: cancelled during grace.
 *  - rejected: refused with a recorded reason (the banned-evader / retention-conflict adversarial
 *    case: a confirmed bad actor cannot use erasure to wipe the fraud / AML evidence they are one).
 */
export type ErasureStatus = 'requested' | 'executed' | 'cancelled' | 'rejected';

/**
 * One right-to-be-forgotten request (D-erasure, Phase 7 surface 6). A FIRST-CLASS entity (the DSAR /
 * ticket precedent), anchored on the subject member. The grace deadline is DERIVED off the injected
 * clock (requestedAt plus GRACE_WINDOW_MS), NOT a stored flag and NO deletion engine. The handling
 * actions (request / cancel / reject / execute) route through the chokepoint or a guarded handler and
 * are audited; the acting admin is recorded by the audit floor, never stored on the row. The real
 * data-subject-erasure system (the irreversible cascade) is the eventual backend home. [EXTENSION]
 */
export interface ErasureRequest extends RaRecord<number> {
  /** FK -> users.id: the member whose data the request is to erase. */
  subjectUserId: number;
  /** Denormalised mirror of the subject's market, for the region facet. */
  market: Market;
  status: ErasureStatus;
  /** When the request was lodged: the grace clock starts here. */
  requestedAt: string;
  /** When the irreversible execute fired (the tombstone), or null. */
  executedAt: string | null;
  /** When the request was cancelled during grace, or null. */
  cancelledAt: string | null;
  /** When the request was rejected, or null. */
  rejectedAt: string | null;
  /**
   * The recorded WHY for a rejection, captured via the action so it lands in the audit trail.
   * MANDATORY at rejection (refusing erasure must be defensible); null otherwise.
   */
  rejectionReason: string | null;
}

/**
 * The marker an erased User row's identifying fields are scrubbed to on execute (D-erasure). A
 * recognisable tombstone value, so the row reads as deliberately erased rather than blank-by-accident.
 */
export const ERASED_MARKER = '[erased]';

// --- settings (Phase 9, S1) -------------------------------------------------

/**
 * System settings config, ONE ROW PER MARKET (D-settings-s1, Phase 9). The FIRST
 * SINGLETON-CONFIG surface in the build: a fixed set of config fields on a
 * per-market row, edited IN PLACE (NOT a freely-added key-value store; the schema
 * is fixed). Region-keyed so a GLOBAL admin editing "both" performs two audited
 * per-region writes, each region legible in its own audit entry. The low-stakes
 * fields (siteName, currencyDisplay) take AUTOMATIC AUDIT only; cashbackDefault
 * (the value affecting what members earn) forces a reason via settings:configure,
 * captured into cashbackChangeReason so it lands in the audit diff. The live apps
 * reading these settings are the migration seam. [EXTENSION — mock-leads, no
 * backbone settings table.]
 */
export interface SystemSetting extends RaRecord<number> {
  market: Market;
  siteName: string;
  /** The market's display currency (uk->gbp, us->usd): the currency default. */
  currencyDisplay: Currency;
  /** The default cashback rate for the market, a percentage. Reason-forced. */
  cashbackDefault: number;
  /** The captured WHY of the last cashback-default change (the fraudReason idiom). */
  cashbackChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * A feature flag, PER-MARKET (D-settings-s1). A flag can be on in uk and off in us.
 * Toggling FORCES a reason (the hard rule "flag changes write to audit with
 * reason"), captured via JustifiedAction and stored as lastChangeReason so it
 * lands in the audit diff. Gated feature_flags:write at editor (a distinct
 * capability so it can be tightened later). [EXTENSION — mock-leads, no backbone
 * feature_flags table.]
 */
export interface FeatureFlag extends RaRecord<number> {
  /** The flag identifier, e.g. 'new-dashboard'. Shared across a flag's per-market rows. */
  key: string;
  label: string;
  description: string;
  market: Market;
  enabled: boolean;
  /** The captured WHY of the last toggle (the fraudReason idiom). */
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/** The Plus marketing staged-activation lifecycle (dormant the launch default). */
export type PlusMarketingState = 'dormant' | 'partial' | 'active';

/**
 * The Plus marketing flag, PER-MARKET (D-settings-s1). The staged-activation
 * lifecycle: dormant -> partial -> active. Changing it FORCES a reason and is
 * SUPER_ADMIN-only (marketing_flags:write): flipping platform-wide marketing live
 * is consequential beyond a feature toggle, so it sits above editor with the
 * financial-authorisation / audit-oversight tier. [EXTENSION — mock-leads.]
 */
export interface PlusMarketingFlag extends RaRecord<number> {
  market: Market;
  state: PlusMarketingState;
  /** The captured WHY of the last state change (the fraudReason idiom). */
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * A transactional email template (D-settings-s3-email-templates, Phase 9 S3). A FIXED
 * set of template TYPES (a backend SES sender contract), EDIT-ONLY (no create / delete),
 * region-varianted as ONE ROW PER (templateKey, market) so copy differs per market
 * (currency, tone, legal footer). The body is PLAIN text carrying `{{placeholders}}`
 * (the flat SEO-rules shape), NOT rich text: an SES email is a backend-owned HTML
 * template with merge fields, not marketing prose, so TipTap is deliberately not reused;
 * the HTML render and send are the migration seam. The DECLARED variables are NOT stored
 * here: they live in the EMAIL_TEMPLATE_TYPES const contract, looked up by templateKey
 * (one source of truth, no per-market drift). [EXTENSION — mock-leads, no backbone table.]
 */
export interface EmailTemplate extends RaRecord<number> {
  /** The fixed template type key (see EMAIL_TEMPLATE_TYPES in emailTemplatesModel). */
  templateKey: string;
  market: Market;
  /** The subject line, may contain `{{variables}}`. */
  subject: string;
  /** The plain-text body carrying the copy and `{{placeholders}}`. */
  body: string;
  updatedBy: string;
  updatedAt: string;
}

/**
 * US COMPLIANCE settings (D-settings-s4-us-compliance, Phase 9 S4), the last and
 * HIGHEST-CONSEQUENCE Settings surface. US-ONLY by construction (the 1099-K is an IRS
 * form, the state privacy laws are US, the privacy / terms are the US legal documents),
 * so every row carries a fixed market 'us': there is NO uk equivalent and NO cross-region
 * apply-both. Gated compliance_settings:write (compliance + super_admin, never editor);
 * every change forces a reason. [EXTENSION — mock-leads, no backbone tables.]
 */

/**
 * The 1099-K reporting threshold for one tax YEAR, promoted from the hardcoded
 * THRESHOLD_BY_YEAR constant to editable config. This is the ONE compliance setting with
 * a LIVE in-build consumer: the tax report (buildTaxReportView) READS these rows as its
 * crossing gate, so editing a year's threshold flows through to which members the report
 * lists. THRESHOLD_BY_YEAR demotes to the SEED DEFAULT (the figures these rows seed from)
 * and the code FALLBACK (an absent config still renders the statutory figure, never blank).
 * One row per year so each figure audits independently with its own forced reason.
 */
export interface ComplianceThreshold extends RaRecord<number> {
  /** Fixed 'us': the 1099-K is a US IRS obligation with no uk equivalent. */
  market: 'us';
  /** The tax year the threshold applies to. */
  year: number;
  /** The IRS 1099-K reporting threshold for the year, in USD. */
  thresholdUsd: number;
  /** The captured WHY of the last change (forced reason, the fraudReason idiom). */
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * A US state privacy-regime toggle: one row per state carrying a comprehensive privacy
 * law, a SINGLE enabled boolean (the opt-out / do-not-sell regime on or off for the
 * state). lawName is descriptive context, NOT a switch. The seeded set is CONTENT-OWNED
 * (a reasonable starting set of states with live laws, NOT an authoritative legal list);
 * new states are ADDED as rows as their laws come live, so it is DATA not a hardcoded
 * enum (the blog-category-vocabulary reason). The consumer (the US app enforcing per-state
 * opt-out) is the migration seam, so this half is author-only. Per-obligation granularity
 * is a deferred refinement (the backend enforces an active regime's obligations).
 */
export interface ComplianceStateToggle extends RaRecord<number> {
  /** Fixed 'us': these are US state laws. */
  market: 'us';
  /** The two-letter US state code, e.g. 'CA'. */
  stateCode: string;
  /** The state privacy law, descriptive context, e.g. 'CCPA / CPRA'. */
  lawName: string;
  /** Whether the state's opt-out / do-not-sell regime is enabled. */
  enabled: boolean;
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * A pointer to the canonical US legal document in the CMS. NOT a document store: the
 * cmsPages legal pages (kind 'legal') own the content and its publish lifecycle; this row
 * records WHICH page is canonical for a role ('privacy' / 'terms') as a slug reference the
 * compliance backend and the US app resolve. Soft-validated against cmsPages (warn if the
 * slug is missing or not kind 'legal'), never a new editor. The seeded legal pages are
 * market 'all', which INCLUDES US, so a US reference at an 'all' page is correct (the
 * reference names the page, the CMS decides the page's market).
 */
export interface ComplianceLegalReference extends RaRecord<number> {
  /** Fixed 'us': the US privacy / terms references. */
  market: 'us';
  /** The legal-document role this reference is for: 'privacy' or 'terms'. */
  referenceKey: string;
  /** The cmsPages slug this points at (a kind 'legal' page). */
  cmsSlug: string;
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * The LOCKED event_type catalog (D-events-explorer, Phase 9), the 25-value backbone
 * `event_type` enum in source order. This is the apps' and backbone's interface: the admin
 * MIRRORS it and does NOT invent types. App lifecycle, schema-block analytics, the member
 * journey, the operational trust events and the search events. [EXTENSION — mock-leads
 * mirroring the backbone events table.]
 */
export const EVENT_TYPES = [
  'app_open',
  'screen_view',
  'tab_switch',
  'error_shown',
  'block_view',
  'block_interact',
  'signup',
  'first_link_click',
  'first_qualifying_transaction',
  'first_payout',
  'welcome_stack_n',
  'store_view',
  'cashback_offer_revealed',
  'upgrade_prompt_shown',
  'upgrade_prompt_tapped',
  'plus_upgrade',
  'plus_downgrade',
  'payment_failed',
  'manual_attribution_pending',
  'manual_attribution_resolved',
  'dsar_filed',
  'fraud_held',
  'search_input',
  'search_result_tap',
  'search_no_results',
] as const;
export type EventType = (typeof EVENT_TYPES)[number];

/**
 * A behavioural event mirroring the backbone `events` table (D-events-explorer, Phase 9).
 * READ-ONLY in the admin: the apps and backend EMIT events, the admin READS and searches
 * them (the real event stream is the migration seam, the admin never emits). The backbone
 * row is { id, user_id (NOT null), event_type, properties jsonb, occurred_at, ... } with
 * NO market column, so `market` here is DERIVED from the event's user and MATERIALIZED on
 * the seeded row (the transactions / cashouts materialized-market pattern) so
 * marketsForScope scopes it. The per-type `properties` bag is the apps' emitted contract
 * (store_view carries store_id / channel_id, block_view carries block_type / block_id /
 * page_id, and so on). [EXTENSION — mock-leads, no anonymous events, user_id required.]
 */
export interface Event extends RaRecord<number> {
  /** The member the event belongs to (the backbone user_id, NOT null). */
  userId: number;
  eventType: EventType;
  /** ISO timestamp the event occurred. */
  occurredAt: string;
  /** The per-type JSONB context bag. */
  properties: Record<string, unknown>;
  /** Derived from the event's user and materialized so the region filter applies. */
  market: Market;
}

/**
 * The LOCKED internal-alert type set (D-internal-alerts, Phase 9), the four documented
 * operational alert types. This is a backend firing-engine contract: the admin CONFIGURES
 * these types and the backend FIRES them, so the admin MIRRORS the set and does NOT invent
 * types (an alert type the backend does not fire would be dead config). failed_payment and
 * fraud_signal are knowable additions the contract can extend to later (a new type is a seed
 * row plus an enum member, the email-template extensibility). [EXTENSION — mock-leads.]
 */
export const ALERT_TYPES = [
  'high_value_cashout',
  'large_refund',
  'suspicious_activity',
  'system_error',
] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

/** A config row's scope: a concrete market (the monetary types) or global (the rest). */
export type AlertMarket = Market | 'global';

/**
 * One alert-type configuration row (D-internal-alerts, Phase 9), the CONFIG half of the
 * internal-alerts surface. The backend firing engine READS these to decide when an alert
 * fires and where it routes. Per-type, with the region split: the MONETARY types
 * (high_value_cashout, large_refund) are REGION-KEYED (one row per market, a per-market
 * `thresholdAmount` in the market currency, since GBP 5,000 is not USD 5,000, the
 * ADJUSTMENT_CAP precedent); the COUNT type (suspicious_activity) is GLOBAL (a
 * `thresholdCount` in a `windowMinutes` window); system_error is GLOBAL with NO threshold
 * (fires on any). The irrelevant threshold fields are null per type. A threshold change
 * FORCES a reason (JustifiedAction); the enabled and channel toggles are plain audited.
 * super_admin-only (alerts:write). [EXTENSION — mock-leads.]
 */
export interface AlertConfig extends RaRecord<number> {
  alertType: AlertType;
  /** A concrete Market for the monetary types, 'global' for the count / none types. */
  market: AlertMarket;
  enabled: boolean;
  /** Route this alert to Slack (the channel config holds the webhook). */
  slackEnabled: boolean;
  /** Route this alert to the email digest (the channel config holds the recipients). */
  emailEnabled: boolean;
  /** Monetary threshold (high_value_cashout / large_refund), in the market currency. */
  thresholdAmount: number | null;
  /** Count threshold (suspicious_activity): N events. */
  thresholdCount: number | null;
  /** The window for the count threshold (suspicious_activity), in minutes. */
  windowMinutes: number | null;
  /** The captured WHY of the last threshold change (forced reason, the fraudReason idiom). */
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * The single global channel configuration (D-internal-alerts, Phase 9): one Slack workspace
 * and one digest recipient list for the whole admin, SEPARATE from the per-type rows (a
 * per-type row says route to slack / email; this holds the actual webhook and recipients).
 * The `slackWebhookUrl` is a SECRET (a Slack incoming-webhook URL is a bearer token: anyone
 * with it can post): it is in SENSITIVE_AUDIT_FIELDS (a change records [redacted], never the
 * value) and write-only in the form (the S2 credential treatment, never read back). The email
 * fields are NOT secret (plain audited). [EXTENSION — mock-leads.]
 */
export interface AlertChannelConfig extends RaRecord<number> {
  /** The Slack incoming-webhook URL. SECRET: redacted in the audit, write-only in the form. */
  slackWebhookUrl: string | null;
  /** The digest recipient addresses (not secret, plain audited). */
  emailRecipients: string[];
  emailFrequency: 'daily' | 'weekly';
  /** The captured WHY of the last webhook change (forced reason, the fraudReason idiom). */
  lastChangeReason: string | null;
  updatedBy: string;
  updatedAt: string;
}

/**
 * One FIRED alert instance (D-internal-alerts, Phase 9), the read-only TRAIL half. The
 * backend WRITES these when an alert fires and delivers; the mock SEEDS a representative
 * history. READ-ONLY in the admin (the events-explorer read-only-list-with-detail shape):
 * the admin views what fired, it never fires or delivers. The real firing and delivery is
 * the migration seam. [EXTENSION — mock-leads.]
 */
export interface FiredAlert extends RaRecord<number> {
  alertType: AlertType;
  /** ISO timestamp the alert fired. */
  firedAt: string;
  /** The context bag: what tripped it (e.g. { cashoutId, amount } or { eventSummary }). */
  trigger: Record<string, unknown>;
  /** Which channels the backend delivered to (slack / email). */
  channelsDelivered: string[];
  market: AlertMarket;
}

// --- dataset ----------------------------------------------------------------

/** The full mock dataset, keyed by resource name. */
export interface Dataset {
  merchants: Merchant[];
  merchantRegions: MerchantRegion[];
  storeChannels: StoreChannel[];
  storeTags: StoreTag[];
  merchantRegionTags: MerchantRegionTag[];
  affiliateNetworks: AffiliateNetwork[];
  affiliateNetworkMarkets: AffiliateNetworkMarket[];
  users: User[];
  transactions: Transaction[];
  cashoutRequests: CashoutRequest[];
  /**
   * The signed BALANCE LEDGER (D-balance-adjustment). Chargeback reversals and manual
   * adjustments both live here, and deriveBalanceSigned sums them. OPTIONAL: the
   * provider defensively treats an absent collection as empty (and auto-creates it on
   * first write), so a fixture that exercises neither can omit it. [EXTENSION]
   */
  balanceLedger?: BalanceLedgerEntry[];
  /**
   * Support tickets (D-ticket-inbox, Phase 6). OPTIONAL: the provider treats an absent
   * collection as empty (and auto-creates it on first write), so a fixture that does
   * not exercise tickets can omit it. [EXTENSION]
   */
  tickets?: Ticket[];
  /**
   * Canned response + macro library (D-canned-responses, Phase 6). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write), so a fixture
   * that does not exercise templates can omit it. A SHARED GLOBAL library, not market-
   * tagged in v1. [EXTENSION]
   */
  cannedResponses?: CannedResponse[];
  /**
   * Recorded view-only impersonation sessions (D-impersonation, Phase 6). OPTIONAL: the
   * provider treats an absent collection as empty (and auto-creates it on first write), so
   * a fixture that does not exercise impersonation can omit it. [EXTENSION]
   */
  impersonationSessions?: ImpersonationSession[];
  /**
   * Data-subject access requests (D-dsar, Phase 7). OPTIONAL: the provider treats an absent
   * collection as empty (and auto-creates it on first write), so a fixture that does not
   * exercise DSARs can omit it. [EXTENSION]
   */
  dsarRequests?: DsarRequest[];
  /**
   * Append-only consent records (D-consent-records, Phase 7). OPTIONAL: the provider treats an
   * absent collection as empty (and auto-creates it on first write), so a fixture that does not
   * exercise consent can omit it. [EXTENSION]
   */
  consents?: ConsentRecord[];
  /**
   * Engine-written sanctions matches (D-sanctions, Phase 7). OPTIONAL: the provider treats an
   * absent collection as empty (and auto-creates it on first write), so a fixture that does not
   * exercise sanctions can omit it. [EXTENSION]
   */
  sanctionsMatches?: SanctionsMatch[];
  /**
   * Right-to-be-forgotten requests (D-erasure, Phase 7). OPTIONAL: the provider treats an absent
   * collection as empty (and auto-creates it on first write), so a fixture that does not exercise
   * erasure can omit it. [EXTENSION]
   */
  erasureRequests?: ErasureRequest[];
  /**
   * Rate rules (D-rate-rules, Phase 8). OPTIONAL: the provider treats an absent collection
   * as empty (and auto-creates it on first write), so a fixture that does not exercise rate
   * rules can omit it. [EXTENSION]
   */
  rateRules?: RateRule[];
  /**
   * Schema-driven page drafts (D-page-schema-editor-8a, Phase 8). OPTIONAL: the
   * provider treats an absent collection as empty (and auto-creates it on first
   * write), so a fixture that does not exercise page schemas can omit it. [EXTENSION]
   */
  pageSchemas?: PageSchema[];
  /**
   * Conventional rich-text CMS pages (D-cms-pages-core, Phase 8). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write), so a
   * fixture that does not exercise the CMS can omit it. SEPARATE from pageSchemas (rich
   * text, not blocks). [EXTENSION]
   */
  cmsPages?: CmsPage[];
  /**
   * Conventional rich-text blog posts (D-cms-blog, Phase 8). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write), so a
   * fixture that does not exercise the blog can omit it. REUSES the cmsPages body and
   * lifecycle in a SEPARATE collection. [EXTENSION]
   */
  blogPosts?: BlogPost[];
  /**
   * The managed editorial-category vocabulary for the blog (D-cms-blog, Phase 8). A
   * platform-wide reference list cloned from storeTags (immutable key, no delete).
   * OPTIONAL: the provider treats an absent collection as empty (and auto-creates it on
   * first write), so a fixture that does not exercise the blog can omit it. [EXTENSION]
   */
  blogCategories?: BlogCategory[];
  /**
   * Marketing-site testimonials (D-cms-testimonials-seo, Phase 8). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write), so a
   * fixture that does not exercise testimonials can omit it. A FLAT structured record (no
   * rich text, no publish), the active / inactive toggle. [EXTENSION]
   */
  testimonials?: Testimonial[];
  /**
   * Per-page SEO meta rules (D-cms-testimonials-seo, Phase 8). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write), so a
   * fixture that does not exercise SEO rules can omit it. Keyed by a FREE pageKey / path
   * (no FK), with per-market region variants. [EXTENSION]
   */
  seoRules?: SeoRule[];
  /**
   * Marketing-site slider slides (D-cms-sliders, Phase 8). OPTIONAL: the provider treats
   * an absent collection as empty (and auto-creates it on first write), so a fixture that
   * does not exercise sliders can omit it. Placement-grouped with a per-placement order,
   * the active toggle, no publish. [EXTENSION]
   */
  sliderSlides?: SliderSlide[];
  /**
   * Navigation menu items (D-cms-menu, Phase 8, the LAST CMS piece). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write), so a fixture
   * that does not exercise the menu can omit it. An adjacency-list tree (parentId + order)
   * capped at 2 levels, grouped by a free location key, the active toggle, no publish.
   * [EXTENSION]
   */
  menuItems?: MenuItem[];
  /**
   * Brick-and-mortar / card-linked-offer venues (D-offline-stores, Phase 8). OPTIONAL: the
   * provider treats an absent collection as empty (and auto-creates it on first write), so a
   * fixture that does not exercise offline stores can omit it. Its own collection, one row
   * per venue, the offline OB / CLO tracking axis. [EXTENSION]
   */
  offlineStores?: OfflineStore[];
  /**
   * System settings config, ONE ROW PER MARKET (D-settings-s1, Phase 9). The first
   * singleton-config surface. OPTIONAL: the provider treats an absent collection as empty
   * (and auto-creates it on first write), so a fixture that does not exercise settings can
   * omit it. [EXTENSION]
   */
  systemSettings?: SystemSetting[];
  /**
   * Feature flags, per-market on/off rows (D-settings-s1, Phase 9). OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write). Toggling
   * forces a reason. [EXTENSION]
   */
  featureFlags?: FeatureFlag[];
  /**
   * The Plus marketing flag, per-market staged-activation rows (D-settings-s1, Phase 9).
   * OPTIONAL: the provider treats an absent collection as empty (and auto-creates it on
   * first write). Changing it is super_admin-only and forces a reason. [EXTENSION]
   */
  plusMarketingFlags?: PlusMarketingFlag[];
  /**
   * Transactional email templates, one row per (templateKey, market) (D-settings-s3-email-
   * templates, Phase 9). OPTIONAL: the provider treats an absent collection as empty (and
   * auto-creates it on first write), so a fixture that does not exercise templates can omit
   * it. A fixed set, edit-only. [EXTENSION]
   */
  emailTemplates?: EmailTemplate[];
  /**
   * US 1099-K reporting thresholds, one row per tax year (D-settings-s4-us-compliance,
   * Phase 9). The tax report READS these as its crossing gate. OPTIONAL: the provider
   * treats an absent collection as empty (and auto-creates it on first write); an absent
   * collection means the tax report falls back to the statutory THRESHOLD_BY_YEAR, never
   * a blank threshold. [EXTENSION]
   */
  complianceThresholds?: ComplianceThreshold[];
  /**
   * US state privacy-regime toggles, one row per state (D-settings-s4-us-compliance,
   * Phase 9). OPTIONAL: absent-is-empty. A single enabled boolean per state; the consumer
   * (the US app enforcing per-state opt-out) is the migration seam. [EXTENSION]
   */
  complianceStateToggles?: ComplianceStateToggle[];
  /**
   * US legal-document references into the CMS legal pages, one row per document role
   * (D-settings-s4-us-compliance, Phase 9). OPTIONAL: absent-is-empty. A slug pointer, NOT
   * a document store. [EXTENSION]
   */
  complianceLegalReferences?: ComplianceLegalReference[];
  /**
   * Behavioural events mirroring the backbone events table (D-events-explorer, Phase 9).
   * OPTIONAL: the provider treats an absent collection as empty (and auto-creates it on
   * first write). READ-ONLY in the admin; the real app / backend event stream is the
   * migration seam. [EXTENSION]
   */
  events?: Event[];
  /**
   * Internal-alert per-type configuration (D-internal-alerts, Phase 9). OPTIONAL:
   * absent-is-empty. The backend firing engine reads these. [EXTENSION]
   */
  alertConfigs?: AlertConfig[];
  /**
   * The single global alert channel configuration (D-internal-alerts, Phase 9). OPTIONAL:
   * absent-is-empty. Holds the redacted Slack webhook and the email routing. [EXTENSION]
   */
  alertChannelConfigs?: AlertChannelConfig[];
  /**
   * The fired-alert trail (D-internal-alerts, Phase 9). OPTIONAL: absent-is-empty.
   * READ-ONLY: the backend writes it, the mock seeds a representative history. [EXTENSION]
   */
  firedAlerts?: FiredAlert[];
  welcomeBonusFundings: WelcomeBonusFunding[];
  charities: Charity[];
  charityTypes: CharityType[];
  categories: Category[];
  clicks: Click[];
  promoCodes: PromoCode[];
  placements: Placement[];
  /** Historical audit entries (seeded); the wrapper appends to this at runtime. */
  auditLog: AuditLogEntry[];
}

// --- store detail (assembled read model) ------------------------------------

/** One channel within a market section of the assembled store detail (read-only). */
export interface StoreDetailChannel {
  id: number;
  /** The merchant_regions row this channel hangs off — the row a channel edit writes. */
  merchantRegionId: number;
  channelType: ChannelType;
  label: string;
  baseRate: number;
  trackingUrl: string | null;
  /** Override of the merchant's attribution method for this channel, or null to inherit. */
  attributionMethodOverride: AttributionMethod | null;
  isActive: boolean;
}

/**
 * A tag assigned to a market section of the store detail. `assignmentId` is the
 * merchant_region_tags JOIN row id (the row a detach deletes); `tagId` is the
 * storeTags vocabulary id. The join is the source of truth — the denormalised
 * tagKeys mirror is derived from it on read and cannot drift (D-multi-channel-editor).
 */
export interface StoreDetailTag {
  /** merchant_region_tags.id — the join row a detach removes. */
  assignmentId: number;
  /** storeTags.id — the vocabulary tag. */
  tagId: number;
  key: string;
  label: string;
}

/**
 * One market section of the assembled store detail — the per-market
 * `merchant_regions` row plus its read-only channels and tags. `regionId` is the
 * `merchant_regions.id`: editing this section's rate/active writes THIS row (the
 * section, not the switcher, determines the row — D2-6).
 */
export interface StoreDetailMarket {
  regionId: number;
  market: Market;
  baseRate: number;
  active: boolean;
  networkId: number | null;
  networkName: string | null;
  channels: StoreDetailChannel[];
  tags: StoreDetailTag[];
}

/**
 * The assembled store detail (`getOne('stores', merchantId)`), built by the
 * provider from a merchant + all its `merchant_regions` (+ channels, tags,
 * network names). `id` is the MERCHANT id (the brand), so the detail/edit are
 * keyed by brand while the list stays per-market. Editable fields are the brand
 * descriptive set + per-market `baseRate`/`active` (D2-3); everything else
 * (channels, tags, network) is read-only this pass. The component filters
 * `markets` to those the user may see (D2-2).
 */
export interface StoreDetail extends RaRecord<number> {
  name: string;
  slug: string;
  description: string;
  logoUrl: string | null;
  categoryId: number | null;
  category: string;
  coarseCategory: MerchantCategory;
  founderNames: string[];
  countryOfHeritage: string;
  blackOwnershipEvidenceUrl: string;
  ownershipVerificationStatus: OwnershipVerificationStatus;
  status: MerchantStatus;
  createdAt: string;
  markets: StoreDetailMarket[];
}

// --- dashboard aggregates ---------------------------------------------------

/**
 * Region-scoped dashboard aggregates, computed by the provider seam
 * (`getDashboardStats`) — NOT in the component. The future real admin API
 * implements the same contract, and a future trend chart consumes the same
 * seam, so neither forces a dashboard rework (see DECISIONS.md D-dashboard).
 *
 * `region` is always a concrete access scope (`UK` / `US`) here — the dashboard
 * is viewed for one active region — so a single `market` / `currency` applies.
 */
export interface DashboardStats {
  market: Market;
  currency: Currency;
  /** Members (users) in the active market. */
  members: number;
  /** merchant_regions active in the active market. */
  activeStores: number;
  /** Sum of cashbackAmount for pending + waiting transactions (market currency). */
  pendingCashback: number;
  /** Sum of cashbackAmount for confirmed transactions (market currency). */
  confirmedCashback: number;
  /** Cashout requests awaiting action (requested + processing). */
  cashoutsAwaitingAction: number;
  cashoutsRequested: number;
  cashoutsProcessing: number;
  /** Cashout requests whose SLA has been breached (slaBreachedAt set). */
  slaBreaches: number;
}

/**
 * The app's DataProvider: the standard React Admin interface plus the custom
 * read-only aggregate seam. Surfaces type the provider as this to call
 * `getDashboardStats`.
 */
export type AdminDataProvider = DataProvider & {
  getDashboardStats: (params: {
    region: string;
  }) => Promise<{ data: DashboardStats }>;
};
